// ProjectTasksPage.jsx - Supervisor Project Notes / To-Do (Weekly)
// Location: src/pages/planning/ProjectTasksPage.jsx
//
// Parent task rows with inline editing.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight, AlertCircle, AlertTriangle, CheckCircle2, ClipboardList, RotateCcw, X, ArrowUpDown, Filter, ChevronUp, ChevronDown, Info, TrendingUp, Settings2, Download, Upload, FileText, FileSpreadsheet, File, GripVertical, Layers, Home, Maximize2, Minimize2, Hash, ZoomIn, ZoomOut } from 'lucide-react';
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors, useDroppable, useDraggable, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useProjectWeeklyTasks } from '../../hooks/useProjectWeeklyTasks';
import { useHFBProjectAllocations } from '../../hooks/useHFBData';
import { useProjectTaskGroups } from '../../hooks/useProjectTaskGroups';
import { useProjectTaskViewState } from '../../hooks/useProjectTaskViewState';
import { BrioDatePicker } from '../../components/BrioComponents';


// ══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════

const BRIO_GREEN = '#689581';
const BRIO_RED = '#934948';
const BRIO_ORANGE = '#D4824A';
const BRIO_DEEP_RED = '#6B2020';
const BRIO_GOLD = '#9f902a';
const BRIO_GREY = '#666666';
const BRIO_BORDER = '#e5e5e5';
const BRIO_BG = '#fafaf8';

// Page zoom levels — CSS zoom applied to table/board content (matches TimelinesPage)
const PAGE_ZOOM_LEVELS = [
  { label: '50%',  scale: 0.50 },
  { label: '75%',  scale: 0.75 },
  { label: '90%',  scale: 0.90 },
  { label: '100%', scale: 1.00 },
  { label: '115%', scale: 1.15 },
  { label: '130%', scale: 1.30 },
  { label: '150%', scale: 1.50 },
];
const PAGE_ZOOM_DEFAULT = 3; // index of 100%

const TASK_CATEGORIES = [
  'Follow up',
  'BRIO',
  'Client',
  'Structure',
  'MEP',
  'Landscape',
  'Consultants',
  'Vendors',
  'Site coordination',
  'PMC',
  'MOM_Linked'
];

var CATEGORY_COLORS = {
  'Follow up':         { color: '#7B6B3A', bg: '#7B6B3A15', border: '#7B6B3A30' },
  'BRIO':              { color: '#9f902a', bg: '#9f902a15', border: '#9f902a30' },
  'Client':            { color: '#4A7A9A', bg: '#4A7A9A15', border: '#4A7A9A30' },
  'Structure':         { color: '#5A7A8A', bg: '#5A7A8A15', border: '#5A7A8A30' },
  'MEP':               { color: '#8B5E3C', bg: '#8B5E3C15', border: '#8B5E3C30' },
  'Landscape':         { color: '#5C8A5A', bg: '#5C8A5A15', border: '#5C8A5A30' },
  'Consultants':       { color: '#7A6890', bg: '#7A689015', border: '#7A689030' },
  'Vendors':           { color: '#8A5A7A', bg: '#8A5A7A15', border: '#8A5A7A30' },
  'Site coordination': { color: '#6A6A8A', bg: '#6A6A8A15', border: '#6A6A8A30' },
  'PMC':               { color: '#934948', bg: '#93494815', border: '#93494830' },
  'MOM_Linked':        { color: '#6B5EA8', bg: '#6B5EA815', border: '#6B5EA830' }
};

// ── Category fuzzy-match helpers ─────────────────────────────────────────────
// Used by CSV/Excel import to match free-text category input to the nearest
// canonical category name even when spelling/casing/abbreviation differs.
function _levenshtein(a, b) {
  var m = a.length, n = b.length;
  var prev = [], cur = [];
  for (var j = 0; j <= n; j++) prev[j] = j;
  for (var i = 1; i <= m; i++) {
    cur[0] = i;
    for (var j = 1; j <= n; j++) {
      cur[j] = a[i-1] === b[j-1] ? prev[j-1] : 1 + Math.min(prev[j], cur[j-1], prev[j-1]);
    }
    prev = cur.slice();
  }
  return prev[n];
}

function _categoryScore(input, key) {
  if (input === key) return 1;
  var minLen = Math.min(input.length, key.length);
  var maxLen = Math.max(input.length, key.length);
  // Containment (one is a substring of the other)
  if (key.includes(input) || input.includes(key)) return 0.85 * minLen / maxLen;
  // Word-token overlap (split on space, underscore, hyphen)
  var aW = input.split(/[\s_\-]+/).filter(Boolean);
  var bW = key.split(/[\s_\-]+/).filter(Boolean);
  var shared = aW.filter(function(w) { return bW.indexOf(w) !== -1; }).length;
  var tokenScore = shared > 0 ? shared / Math.max(aW.length, bW.length) : 0;
  // Levenshtein similarity
  var levSim = maxLen > 0 ? (1 - _levenshtein(input, key) / maxLen) : 0;
  return Math.max(tokenScore, levSim * 0.9);
}

// Returns the canonical category string from categoryLower that best matches
// the raw input text. Falls back to undefined if no match meets the threshold.
function findClosestCategory(rawInput, categoryLower) {
  var q = String(rawInput || '').trim().toLowerCase();
  if (!q) return undefined;
  // 1. Exact match
  if (categoryLower[q] !== undefined) return categoryLower[q];
  // 2. Best fuzzy match
  var bestScore = 0, bestMatch;
  Object.keys(categoryLower).forEach(function(key) {
    var s = _categoryScore(q, key);
    if (s > bestScore) { bestScore = s; bestMatch = categoryLower[key]; }
  });
  // Require at least 45% similarity to avoid wild mismatches
  return bestScore >= 0.45 ? bestMatch : undefined;
}

var PRIORITY_COLORS = [
  { color: '#934948', bg: '#93494815' },
  { color: '#D4824A', bg: '#D4824A15' },
  { color: '#689581', bg: '#68958115' },
  { color: '#B5A020', bg: '#B5A02015' },
  { color: '#5B7A99', bg: '#5B7A9915' },
  { color: '#8B7BA3', bg: '#8B7BA315' }
];

var DEFAULT_PRIORITY_LABELS = ['Urgent', 'High', 'Medium', 'Low'];

function buildPriorityOptions(labels) {
  return (labels || DEFAULT_PRIORITY_LABELS).map(function (label, i) {
    var c = PRIORITY_COLORS[i % PRIORITY_COLORS.length];
    return { value: label, color: c.color, bg: c.bg };
  });
}

// ══════════════════════════════════════════════════════════════════════════
// WEEK HELPERS (same ISO week logic as HFB)
// ══════════════════════════════════════════════════════════════════════════

function getCurrentWeekId() {
  var now = new Date();
  var d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  var dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  var yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  var weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return d.getUTCFullYear() + '-W' + String(weekNum).padStart(2, '0');
}

function offsetWeekId(weekId, offset) {
  var parts = weekId.split('-W');
  var year = Number(parts[0]);
  var week = Number(parts[1]);
  var jan4 = new Date(Date.UTC(year, 0, 4));
  var dayOfWeek = jan4.getUTCDay() || 7;
  var week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
  var targetMonday = new Date(week1Monday);
  targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7 + offset * 7);
  var d2 = new Date(targetMonday);
  var dn = d2.getUTCDay() || 7;
  d2.setUTCDate(d2.getUTCDate() + 4 - dn);
  var ys = new Date(Date.UTC(d2.getUTCFullYear(), 0, 1));
  var wn = Math.ceil((((d2 - ys) / 86400000) + 1) / 7);
  return d2.getUTCFullYear() + '-W' + String(wn).padStart(2, '0');
}

function weekIdToDateRange(weekId) {
  var parts = weekId.split('-W');
  var year = Number(parts[0]);
  var week = Number(parts[1]);
  var jan4 = new Date(Date.UTC(year, 0, 4));
  var dayOfWeek = jan4.getUTCDay() || 7;
  var week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
  var monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  var sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  var fmt = function (d) {
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  };
  return fmt(monday) + ' – ' + fmt(sunday);
}

function getWeekTabs(centerWeekId) {
  var tabs = [];
  for (var i = -2; i <= 2; i++) {
    tabs.push(offsetWeekId(centerWeekId, i));
  }
  return tabs;
}

// ══════════════════════════════════════════════════════════════════════════
// PROGRESS COLOR LOGIC
// ══════════════════════════════════════════════════════════════════════════

function getProgressColor(progress, completionDate) {
  if (completionDate && progress < 100) {
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var due = new Date(completionDate + 'T00:00:00');
    if (today > due) return BRIO_DEEP_RED;
  }
  if (progress === 100) return BRIO_GREEN;
  if (progress >= 50) return BRIO_ORANGE;
  return BRIO_RED;
}

function getProgressBg(progress, completionDate) {
  var color = getProgressColor(progress, completionDate);
  if (color === BRIO_DEEP_RED) return '#6B202020';
  if (color === BRIO_GREEN) return '#68958120';
  if (color === BRIO_ORANGE) return '#D4824A20';
  return '#93494820';
}

function isOverdueCheck(completionDate, progress) {
  if (!completionDate || progress >= 100) return false;
  var today = new Date();
  today.setHours(0, 0, 0, 0);
  var due = new Date(completionDate + 'T00:00:00');
  return today > due;
}

// ══════════════════════════════════════════════════════════════════════════
// INLINE EDITABLE CELL COMPONENTS
// ══════════════════════════════════════════════════════════════════════════

// Default labels that should auto-clear on focus
var AUTO_CLEAR_DEFAULTS = ['new task', 'new subtask'];

function isAutoClearDefault(val) {
  if (!val) return false;
  return AUTO_CLEAR_DEFAULTS.indexOf(val.trim().toLowerCase()) !== -1;
}

// Auto-expanding textarea that looks like a plain text input
// Render each line of text with hanging-indent for bullet/numbered lines.
// Used by InlineTextCell display mode for both read-only and edit-idle states.
function renderHangingLines(text, placeholderEl) {
  var hasContent = text && text.trim();
  if (!hasContent) return placeholderEl;
  return text.split('\n').map(function (rawLine, i) {
    var line = rawLine.replace(/^- /, '• ');           // normalise "- " → "• "
    var m = line.match(/^([•]\s|\d+\.\s)/);            // bullet or "N. " prefix
    if (m) {
      var prefix = m[0];
      var rest   = line.slice(prefix.length);
      // Two-column flex: prefix column is its natural width (flexShrink:0),
      // text column fills the rest (flex:1 minWidth:0). When the text column
      // wraps, continuation lines start under the first character of the text —
      // not under the bullet — regardless of how wide the prefix is.
      return (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start' }}>
          <span style={{ flexShrink: 0, whiteSpace: 'pre' }}>{prefix}</span>
          <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{rest || '\u00a0'}</span>
        </div>
      );
    }
    return <div key={i} style={{ wordBreak: 'break-word' }}>{line || '\u00a0'}</div>;
  });
}

// Shared outer-div style for display mode.
function displayCellStyle(hasContent, extraStyle) {
  return {
    width: '100%',
    fontFamily: 'Raleway, sans-serif',
    fontSize: '13px',
    fontWeight: 300,
    padding: '6px 4px',
    color: hasContent ? '#333' : '#bbb',
    lineHeight: '1.4',
    ...extraStyle,
  };
}

function InlineTextCell({ value, onChange, placeholder, style, readOnly, autoClearDefaults }) {
  var [localVal, setLocalVal] = useState(value || '');
  var [isEditing, setIsEditing] = useState(false);
  var textareaRef = useRef(null);

  useEffect(function () { setLocalVal(value || ''); }, [value]);

  // Auto-resize textarea height to fit content
  var autoResize = useCallback(function () {
    var el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);

  // Focus + resize as soon as editing mode activates
  useEffect(function () {
    if (isEditing && textareaRef.current) {
      autoResize();
      textareaRef.current.focus();
      var len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }, [isEditing, autoResize]);

  // Keep textarea height in sync while the user types
  useEffect(function () { if (isEditing) autoResize(); }, [localVal, autoResize, isEditing]);

  var commit = function () {
    setIsEditing(false);
    if (localVal !== (value || '')) onChange(localVal);
  };

  var startEditing = function () {
    if (readOnly) return;
    if (autoClearDefaults && isAutoClearDefault(localVal)) setLocalVal('');
    setIsEditing(true);
  };

  // ── Textarea — shown only while actively editing ──────────────────────
  if (!readOnly && isEditing) {
    return (
      <textarea
        ref={textareaRef}
        value={localVal}
        placeholder={placeholder}
        rows={1}
        onChange={function (e) {
          var v = e.target.value.replace(/(^|\n)- /g, '$1• ');
          setLocalVal(v);
        }}
        onBlur={commit}
        onKeyDown={function (e) {
          if (e.key === 'Escape') { setLocalVal(value || ''); setIsEditing(false); return; }
          if (e.key === 'Enter') {
            if (e.altKey) {
              // Alt+Enter — insert newline and continue the list style
              e.preventDefault();
              var ta = e.target;
              var start = ta.selectionStart;
              var end   = ta.selectionEnd;
              var before = localVal.substring(0, start);
              var after  = localVal.substring(end);
              var lastNL = before.lastIndexOf('\n');
              var curLine = before.substring(lastNL + 1);
              var pfx = '';
              var nm = curLine.match(/^(\d+)\.\s/);
              if (nm) {
                pfx = (parseInt(nm[1], 10) + 1) + '. ';
              } else if (curLine.match(/^[•\-]\s/)) {
                pfx = '• ';
              }
              var ins = '\n' + pfx;
              var nv  = before + ins + after;
              var nc  = start + ins.length;
              setLocalVal(nv);
              setTimeout(function () { ta.setSelectionRange(nc, nc); }, 0);
              return;
            }
            // Plain Enter commits
            e.preventDefault();
            e.target.blur();
          }
        }}
        style={{
          width: '100%', border: 'none', background: 'transparent',
          fontFamily: 'Raleway, sans-serif', fontSize: '13px', fontWeight: 300,
          padding: '6px 4px', color: '#333', outline: 'none',
          resize: 'none', overflow: 'hidden', lineHeight: '1.4', display: 'block',
          ...style,
        }}
      />
    );
  }

  // ── Display mode — shown when read-only OR not currently editing ───────
  // Clicking enters edit mode (for editable cells); styled hanging-indent
  // view is always visible so bullet/numbered lists look correct.
  var hasContent = value && value.trim();
  return (
    <div
      style={{ ...displayCellStyle(hasContent, style), cursor: readOnly ? 'default' : 'text' }}
      onClick={readOnly ? undefined : startEditing}
    >
      {renderHangingLines(value, placeholder || '—')}
    </div>
  );
}

function InlineDateCell({ value, onChange, readOnly }) {
  if (readOnly) {
    var displayVal = value
      ? new Date(value + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
      : '—';
    return (
      <div style={{
        fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
        padding: '6px 2px', color: value ? '#333' : '#bbb'
      }}>
        {displayVal}
      </div>
    );
  }
  return (
    <BrioDatePicker
      value={value || ''}
      onChange={function (v) { onChange(v || null); }}
      clearable={!!value}
      placeholder=""
      style={{ width: '100%' }}
      triggerStyle={{ boxSizing: 'border-box', width: '100%', justifyContent: 'center' }}
      className="brio-datepicker-trigger--borderless"
    />
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SHARED: fixed-position dropdown helper
// Dropdowns use position:fixed so they escape overflow:auto table wrappers.
// ══════════════════════════════════════════════════════════════════════════

// Returns {top, left, openAbove} for a fixed-position dropdown panel.
// Opens below the trigger normally; flips above when there is insufficient
// space below the viewport fold.  When openAbove is true the panel should
// use  transform: translateY(-100%)  so it grows upward from the trigger.
// Also closes the dropdown on any scroll (captures inner scrollers via useCapture).
function useFixedDropdownPos(isOpen, triggerRef, setIsOpen) {
  var [pos, setPos] = useState({ top: 0, left: 0, openAbove: false });
  useEffect(function () {
    if (!isOpen || !triggerRef.current) return;
    var rect = triggerRef.current.getBoundingClientRect();
    var spaceBelow = window.innerHeight - rect.bottom - 8;
    var spaceAbove = rect.top - 8;
    var openAbove = spaceBelow < 150 && spaceAbove > spaceBelow;
    setPos({
      top: openAbove ? (rect.top - 2) : (rect.bottom + 2),
      left: rect.left,
      openAbove: openAbove
    });
    function onScroll(e) {
      // Only close when the trigger's ancestor scrolled (i.e. the trigger moved in
      // the viewport).  Ignore scroll events that originate inside the dropdown
      // panel itself — those are the user scrolling the dropdown list or using the
      // dropdown scrollbar, and closing here is what caused the bug.
      var scrolledEl = e.target;
      var triggerEl = triggerRef.current;
      var triggerMoved = !triggerEl
        || scrolledEl === document
        || scrolledEl === document.documentElement
        || (typeof scrolledEl.contains === 'function' && scrolledEl.contains(triggerEl));
      if (triggerMoved) setIsOpen(false);
    }
    window.addEventListener('scroll', onScroll, true);
    return function () { window.removeEventListener('scroll', onScroll, true); };
  }, [isOpen, triggerRef, setIsOpen]);
  return pos;
}

// ══════════════════════════════════════════════════════════════════════════
// STATUS SELECT (Not started / In progress / Done)
// ══════════════════════════════════════════════════════════════════════════

var STATUS_OPTIONS = [
  { label: 'Not started', value: 0, color: '#999', bg: '#f0f0f0', border: '#99999930' },
  { label: 'In progress', value: 50, color: '#9F902A', bg: 'rgba(159, 144, 42, 0.1)', border: '#9F902A30' },
  { label: 'Done', value: 100, color: BRIO_GREEN, bg: '#68958115', border: BRIO_GREEN + '30' }
];
// Fixed badge width for all status options — based on the longest label so all buttons are the same width.
// 5.5px per char (10px Raleway) + 12px horizontal padding + 2px border
var STATUS_TAG_MIN_WIDTH = Math.ceil(
  STATUS_OPTIONS.reduce(function (max, o) { return Math.max(max, o.label.length); }, 0) * 5.5 + 14
); // = 75px for "Not started" / "In progress" (11 chars)
// Progress column: td-pad(6) + inner-pad(4) + content + inner-pad(4) + td-pad(6)
// Without icon: content = tag            → col = tag + 20
// With icon:    content = tag + gap(3) + icon(11) → col = tag + 34  (same 10px td-margin each side)
var PROGRESS_COL_NO_ICON   = STATUS_TAG_MIN_WIDTH + 16;
var PROGRESS_COL_WITH_ICON = STATUS_TAG_MIN_WIDTH + 30;

function StatusSelect({ value, completionDate, onChange, readOnly, tagMinWidth, fillWidth }) {
  var [isOpen, setIsOpen] = useState(false);
  var containerRef = useRef(null);
  var dropdownPos = useFixedDropdownPos(isOpen, containerRef, setIsOpen);
  var overdue = isOverdueCheck(completionDate, value);

  useEffect(function () {
    if (!isOpen) return;
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isOpen]);

  var current = STATUS_OPTIONS.find(function (o) { return o.value === value; })
    || (value > 0 && value < 100 ? STATUS_OPTIONS[1] : STATUS_OPTIONS[0]);

  var tagStyle = {
    fontSize: '10px', fontWeight: 400, padding: '2px 6px',
    background: current.bg, color: current.color,
    border: '1px solid ' + current.border, borderRadius: '2px',
    whiteSpace: 'nowrap', lineHeight: '14px',
    minWidth: (tagMinWidth || 60) + 'px', textAlign: 'left',
    display: fillWidth ? 'block' : 'inline-block',
    width: fillWidth ? '100%' : undefined,
    boxSizing: fillWidth ? 'border-box' : undefined,
  };

  if (readOnly) {
    return (
      <div style={{
        display: 'flex', flexWrap: 'nowrap', gap: '3px', padding: '4px 4px',
        minHeight: '28px', alignItems: 'center'
      }}>
        <span style={tagStyle}>{current.label}</span>
        {overdue && (
          <AlertTriangle size={11} strokeWidth={1.8} style={{ color: BRIO_DEEP_RED, flexShrink: 0 }} title="Delayed — past due date" />
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        onClick={function () { setIsOpen(!isOpen); }}
        style={{
          display: 'flex', flexWrap: 'nowrap', gap: '3px', padding: '4px 4px',
          minHeight: '28px', alignItems: 'center', cursor: 'pointer',
          borderBottom: '1px solid ' + (isOpen ? BRIO_GOLD : 'transparent'),
          transition: 'border-color 0.15s'
        }}
        onMouseEnter={function (e) {
          if (!isOpen) e.currentTarget.style.borderBottomColor = BRIO_BORDER;
        }}
        onMouseLeave={function (e) {
          if (!isOpen) e.currentTarget.style.borderBottomColor = 'transparent';
        }}
      >
        <span style={tagStyle}>{current.label}</span>
        {overdue && (
          <AlertTriangle size={11} strokeWidth={1.8} style={{ color: BRIO_DEEP_RED, flexShrink: 0 }} title="Delayed — past due date" />
        )}
      </div>

      {isOpen && (
        <div style={{
          position: 'fixed', top: dropdownPos.top + 'px', left: dropdownPos.left + 'px', zIndex: 9999,
          transform: dropdownPos.openAbove ? 'translateY(-100%)' : undefined,
          background: '#fff', border: '1px solid ' + BRIO_BORDER,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          minWidth: '140px'
        }}>
          {STATUS_OPTIONS.map(function (opt) {
            var isSelected = current.value === opt.value;
            return (
              <label
                key={opt.value}
                onClick={function () { onChange(opt.value); setIsOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 10px', cursor: 'pointer', fontSize: '12px',
                  fontWeight: 300, fontFamily: 'Raleway, sans-serif',
                  color: isSelected ? opt.color : '#555',
                  background: isSelected ? opt.bg : 'transparent',
                  transition: 'background 0.1s'
                }}
                onMouseEnter={function (e) {
                  if (!isSelected) e.currentTarget.style.background = '#f5f5f3';
                }}
                onMouseLeave={function (e) {
                  e.currentTarget.style.background = isSelected ? opt.bg : 'transparent';
                }}
              >
                <span style={{
                  display: 'inline-block', fontSize: '10px', fontWeight: 400, padding: '2px 6px',
                  background: opt.bg, color: opt.color,
                  border: '1px solid ' + opt.border, borderRadius: '2px',
                  whiteSpace: 'nowrap', lineHeight: '14px'
                }}>
                  {opt.label}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ASSIGNED-TO MULTI-SELECT (checkbox dropdown for multiple assignees)
// ══════════════════════════════════════════════════════════════════════════

function AssignedMultiSelect({ value, allocatedUsers, getUserName, getUserColor, allUsers, onChange, readOnly, tagMinWidth }) {
  var selected = Array.isArray(value) ? value.map(String) : [];
  var [isOpen, setIsOpen] = useState(false);
  var containerRef = useRef(null);
  var dropdownPos = useFixedDropdownPos(isOpen, containerRef, setIsOpen);

  useEffect(function () {
    if (!isOpen) return;
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isOpen]);

  var toggleUser = function (userId) {
    var uid = String(userId);
    var next;
    if (selected.indexOf(uid) !== -1) {
      next = selected.filter(function (id) { return id !== uid; });
    } else {
      next = selected.concat(uid);
    }
    onChange(next.map(Number));
  };

  var removeUser = function (userId, e) {
    e.stopPropagation();
    onChange(selected.filter(function (id) { return id !== String(userId); }).map(Number));
  };

  // Build display list: allocated users + any selected users not in allocated list
  var dropdownUsers = allocatedUsers.slice();
  selected.forEach(function (uid) {
    if (!dropdownUsers.find(function (u) { return String(u.id) === uid; })) {
      var found = allUsers && allUsers.find(function (u) { return String(u.id) === uid; });
      if (found) dropdownUsers.push(found);
    }
  });

  if (readOnly) {
    return (
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '3px', padding: '4px 0',
        minHeight: '28px', alignItems: 'center', justifyContent: 'center', maxWidth: '100%'
      }}>
        {selected.length === 0 && (
          <span style={{ fontSize: '12px', fontWeight: 300, color: '#bbb' }}>—</span>
        )}
        {selected.map(function (uid) {
          var uc = getUserColor ? getUserColor(uid) : BRIO_GOLD;
          return (
            <span key={uid} style={{
              fontSize: '10px', fontWeight: 400, padding: '2px 6px',
              background: uc + '15', color: uc,
              border: '1px solid ' + uc + '30', borderRadius: '2px',
              whiteSpace: 'nowrap', lineHeight: '14px',
              textAlign: 'left', display: 'inline-block',
              maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis'
            }}>
              {getUserName(uid)}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        onClick={function () { setIsOpen(!isOpen); }}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: '3px', padding: '4px 0',
          minHeight: '28px', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          maxWidth: '100%',
          borderBottom: '1px solid ' + (isOpen ? BRIO_GOLD : 'transparent'),
          transition: 'border-color 0.15s'
        }}
        onMouseEnter={function (e) {
          if (!isOpen) e.currentTarget.style.borderBottomColor = BRIO_BORDER;
        }}
        onMouseLeave={function (e) {
          if (!isOpen) e.currentTarget.style.borderBottomColor = 'transparent';
        }}
      >
        {selected.length === 0 && (
          <span style={{ fontSize: '12px', fontWeight: 300, color: '#bbb' }}>Select…</span>
        )}
        {selected.map(function (uid) {
          var uc = getUserColor ? getUserColor(uid) : BRIO_GOLD;
          return (
            <span key={uid} style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px',
              fontSize: '10px', fontWeight: 400, padding: '1px 4px 1px 6px',
              background: uc + '15', color: uc,
              border: '1px solid ' + uc + '30', borderRadius: '2px',
              whiteSpace: 'nowrap', lineHeight: '14px',
              minWidth: tagMinWidth ? tagMinWidth + 'px' : undefined
            }}>
              <span>{getUserName(uid)}</span>
              <button
                onClick={function (e) { removeUser(uid, e); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '12px', height: '12px', border: 'none', background: 'transparent',
                  color: uc, cursor: 'pointer', padding: 0
                }}
              >
                <X size={8} />
              </button>
            </span>
          );
        })}
      </div>

      {isOpen && (
        <div
          onMouseLeave={function () { setIsOpen(false); }}
          style={{
          position: 'fixed', top: dropdownPos.top + 'px', left: dropdownPos.left + 'px', zIndex: 9999,
          transform: dropdownPos.openAbove ? 'translateY(-100%)' : undefined,
          background: '#fff', border: '1px solid ' + BRIO_BORDER,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          minWidth: '180px', maxHeight: '240px', overflowY: 'auto'
        }}>
          {dropdownUsers.length === 0 && (
            <div style={{
              padding: '8px 10px', fontSize: '11px', fontWeight: 300,
              color: '#aaa', fontStyle: 'italic'
            }}>
              No team members allocated
            </div>
          )}
          {dropdownUsers.map(function (u) {
            var uid = String(u.id);
            var uc = getUserColor ? getUserColor(uid) : BRIO_GOLD;
            var isSelected = selected.indexOf(uid) !== -1;
            var isAllocated = allocatedUsers.find(function (a) { return String(a.id) === uid; });
            return (
              <label
                key={u.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 10px', cursor: 'pointer', fontSize: '12px',
                  fontWeight: 300, fontFamily: 'Raleway, sans-serif',
                  color: isSelected ? uc : '#555',
                  background: isSelected ? uc + '08' : 'transparent',
                  transition: 'background 0.1s'
                }}
                onMouseEnter={function (e) {
                  if (!isSelected) e.currentTarget.style.background = '#f5f5f3';
                }}
                onMouseLeave={function (e) {
                  e.currentTarget.style.background = isSelected ? uc + '08' : 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={function () { toggleUser(u.id); }}
                  style={{
                    accentColor: uc, width: '13px', height: '13px',
                    cursor: 'pointer', margin: 0
                  }}
                />
                {u.name}
                {!isAllocated && (
                  <span style={{ fontSize: '9px', color: '#aaa', marginLeft: '2px' }}>(not allocated)</span>
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// DELETE BUTTON (shared by parent + subtask rows)
// ══════════════════════════════════════════════════════════════════════════

function DeleteButton({ onClick, title }) {
  return (
    <button
      onClick={onClick}
      title={title || 'Delete'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '26px', height: '26px', borderRadius: '4px',
        border: 'none', background: 'transparent', color: '#ccc',
        cursor: 'pointer', transition: 'color 0.15s, background 0.15s'
      }}
      onMouseEnter={function (e) {
        e.currentTarget.style.color = BRIO_RED;
        e.currentTarget.style.background = '#93494810';
      }}
      onMouseLeave={function (e) {
        e.currentTarget.style.color = '#ccc';
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <Trash2 size={14} />
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// CATEGORY MULTI-SELECT (inline dropdown with checkboxes)
// ══════════════════════════════════════════════════════════════════════════

var SYSTEM_CATEGORIES = ['MOM_Linked', 'timeline_linked'];

function CategoryMultiSelect({ value, onChange, readOnly, tagMinWidth, taskCategories: taskCatsOverride, categoryColors: catColorsOverride }) {
  var TASK_CATEGORIES = taskCatsOverride || ['Follow up','BRIO','Client','Structure','MEP','Landscape','Consultants','Vendors','Site coordination','PMC','MOM_Linked'];
  var CATEGORY_COLORS = catColorsOverride || { 'Follow up': { color: '#7B6B3A', bg: '#7B6B3A15', border: '#7B6B3A30' }, 'BRIO': { color: '#9f902a', bg: '#9f902a15', border: '#9f902a30' }, 'Client': { color: '#4A7A9A', bg: '#4A7A9A15', border: '#4A7A9A30' }, 'Structure': { color: '#5A7A8A', bg: '#5A7A8A15', border: '#5A7A8A30' }, 'MEP': { color: '#8B5E3C', bg: '#8B5E3C15', border: '#8B5E3C30' }, 'Landscape': { color: '#5C8A5A', bg: '#5C8A5A15', border: '#5C8A5A30' }, 'Consultants': { color: '#7A6890', bg: '#7A689015', border: '#7A689030' }, 'Vendors': { color: '#8A5A7A', bg: '#8A5A7A15', border: '#8A5A7A30' }, 'Site coordination': { color: '#6A6A8A', bg: '#6A6A8A15', border: '#6A6A8A30' }, 'PMC': { color: '#934948', bg: '#93494815', border: '#93494830' }, 'MOM_Linked': { color: '#6B5EA8', bg: '#6B5EA815', border: '#6B5EA830' } };
  var selected = Array.isArray(value) ? value : [];
  var [isOpen, setIsOpen] = useState(false);
  var containerRef = useRef(null);
  var dropdownPos = useFixedDropdownPos(isOpen, containerRef, setIsOpen);

  // Close dropdown on outside click
  useEffect(function () {
    if (!isOpen) return;
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isOpen]);

  var toggleCategory = function (cat) {
    var next;
    if (selected.indexOf(cat) !== -1) {
      next = selected.filter(function (c) { return c !== cat; });
    } else {
      next = selected.concat(cat);
    }
    onChange(next);
  };

  var removeCategory = function (cat, e) {
    e.stopPropagation();
    onChange(selected.filter(function (c) { return c !== cat; }));
  };

  if (readOnly) {
    return (
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '3px', padding: '4px 0',
        minHeight: '28px', alignItems: 'center', justifyContent: 'center', minWidth: (tagMinWidth || 60) + 'px'
      }}>
        {selected.length === 0 && (
          <span style={{ fontSize: '12px', fontWeight: 300, color: '#bbb' }}>—</span>
        )}
        {selected.map(function (cat) {
          var cc = CATEGORY_COLORS[cat] || { color: BRIO_GOLD, bg: BRIO_GOLD + '15', border: BRIO_GOLD + '30' };
          return (
            <span key={cat} style={{
              fontSize: '10px', fontWeight: 400, padding: '2px 6px',
              background: cc.bg, color: cc.color,
              border: '1px solid ' + cc.border, borderRadius: '2px',
              whiteSpace: 'nowrap', lineHeight: '14px',
              minWidth: (tagMinWidth || 60) + 'px', textAlign: 'left', display: 'inline-block', flex: 1
            }}>
              {cat}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Trigger area — shows selected tags or placeholder */}
      <div
        onClick={function () { setIsOpen(!isOpen); }}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: '3px', padding: '4px 0',
          minHeight: '28px', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          minWidth: (tagMinWidth || 60) + 'px',
          borderBottom: '1px solid ' + (isOpen ? BRIO_GOLD : 'transparent'),
          transition: 'border-color 0.15s'
        }}
        onMouseEnter={function (e) {
          if (!isOpen) e.currentTarget.style.borderBottomColor = BRIO_BORDER;
        }}
        onMouseLeave={function (e) {
          if (!isOpen) e.currentTarget.style.borderBottomColor = 'transparent';
        }}
      >
        {selected.length === 0 && (
          <span style={{ fontSize: '12px', fontWeight: 300, color: '#bbb' }}>Select…</span>
        )}
        {selected.map(function (cat) {
          var cc = CATEGORY_COLORS[cat] || { color: BRIO_GOLD, bg: BRIO_GOLD + '15', border: BRIO_GOLD + '30' };
          var showDelete = SYSTEM_CATEGORIES.indexOf(cat) === -1;
          return (
            <span key={cat} style={{
              display: 'inline-grid', gridTemplateColumns: showDelete ? '1fr 12px' : '1fr', alignItems: 'center',
              fontSize: '10px', fontWeight: 400, padding: showDelete ? '1px 2px 1px 6px' : '1px 6px',
              background: cc.bg, color: cc.color,
              border: '1px solid ' + cc.border, borderRadius: '2px',
              whiteSpace: 'nowrap', lineHeight: '14px',
              minWidth: (tagMinWidth || 60) + 'px'
            }}>
              <span>{cat}</span>
              {showDelete && (
                <button
                  onClick={function (e) { removeCategory(cat, e); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '12px', height: '12px', border: 'none', background: 'transparent',
                    color: cc.color, cursor: 'pointer', padding: 0
                  }}
                >
                  <X size={8} />
                </button>
              )}
            </span>
          );
        })}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          onMouseLeave={function () { setIsOpen(false); }}
          style={{
          position: 'fixed', top: dropdownPos.top + 'px', left: dropdownPos.left + 'px', zIndex: 9999,
          transform: dropdownPos.openAbove ? 'translateY(-100%)' : undefined,
          background: '#fff', border: '1px solid ' + BRIO_BORDER,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          minWidth: '160px', maxHeight: '240px', overflowY: 'auto'
        }}>
          {TASK_CATEGORIES.filter(function (cat) { return SYSTEM_CATEGORIES.indexOf(cat) === -1; }).map(function (cat) {
            var isSelected = selected.indexOf(cat) !== -1;
            var cc = CATEGORY_COLORS[cat] || { color: BRIO_GOLD, bg: BRIO_GOLD + '15', border: BRIO_GOLD + '30' };
            return (
              <label
                key={cat}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 10px', cursor: 'pointer', fontSize: '12px',
                  fontWeight: 300, fontFamily: 'Raleway, sans-serif',
                  color: isSelected ? cc.color : '#555',
                  background: isSelected ? cc.bg : 'transparent',
                  transition: 'background 0.1s'
                }}
                onMouseEnter={function (e) {
                  if (!isSelected) e.currentTarget.style.background = '#f5f5f3';
                }}
                onMouseLeave={function (e) {
                  e.currentTarget.style.background = isSelected ? cc.bg : 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={function () { toggleCategory(cat); }}
                  style={{
                    accentColor: cc.color, width: '13px', height: '13px',
                    cursor: 'pointer', margin: 0
                  }}
                />
                {cat}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// PRIORITY SINGLE-SELECT (colored pill buttons)
// ══════════════════════════════════════════════════════════════════════════

function PrioritySelect({ value, onChange, readOnly, options: PRIORITY_OPTIONS, tagMinWidth }) {
  var [isOpen, setIsOpen] = useState(false);
  var containerRef = useRef(null);
  var dropdownPos = useFixedDropdownPos(isOpen, containerRef, setIsOpen);

  useEffect(function () {
    if (!isOpen) return;
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); };
  }, [isOpen]);

  var current = PRIORITY_OPTIONS.find(function (o) { return o.value === value; });

  var handleSelect = function (opt) {
    onChange(opt.value === value ? null : opt.value);
    setIsOpen(false);
  };

  if (readOnly) {
    return (
      <div style={{ padding: '4px 4px', minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {current ? (
          <span style={{
            fontSize: '11px', fontWeight: 500, padding: '3px 10px',
            background: current.bg, color: current.color,
            border: '1px solid ' + current.color + '40', borderRadius: '10px',
            whiteSpace: 'nowrap', lineHeight: '16px',
            minWidth: (tagMinWidth || 48) + 'px', textAlign: 'center', display: 'inline-block'
          }}>
            {current.value}
          </span>
        ) : (
          <span style={{ fontSize: '12px', fontWeight: 300, color: '#bbb' }}>—</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        onClick={function () { setIsOpen(!isOpen); }}
        style={{
          padding: '4px 4px', minHeight: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', borderBottom: '1px solid ' + (isOpen ? BRIO_GOLD : 'transparent'),
          transition: 'border-color 0.15s'
        }}
        onMouseEnter={function (e) {
          if (!isOpen) e.currentTarget.style.borderBottomColor = BRIO_BORDER;
        }}
        onMouseLeave={function (e) {
          if (!isOpen) e.currentTarget.style.borderBottomColor = 'transparent';
        }}
      >
        {current ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            fontSize: '11px', fontWeight: 500, padding: '3px 8px 3px 10px',
            background: current.bg, color: current.color,
            border: '1px solid ' + current.color + '40', borderRadius: '10px',
            whiteSpace: 'nowrap', lineHeight: '16px',
            minWidth: (tagMinWidth || 48) + 'px', textAlign: 'center'
          }}>
            {current.value}
            <button
              onClick={function (e) { e.stopPropagation(); onChange(null); }}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '12px', height: '12px', border: 'none', background: 'transparent',
                color: current.color, cursor: 'pointer', padding: 0
              }}
            >
              <X size={8} />
            </button>
          </span>
        ) : (
          <span style={{ fontSize: '12px', fontWeight: 300, color: '#bbb' }}>Select…</span>
        )}
      </div>

      {isOpen && (
        <div style={{
          position: 'fixed', top: dropdownPos.top + 'px', left: dropdownPos.left + 'px', zIndex: 9999,
          transform: dropdownPos.openAbove ? 'translateY(-100%)' : undefined,
          background: '#fff', border: '1px solid ' + BRIO_BORDER,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          minWidth: '120px', padding: '4px'
        }}>
          {PRIORITY_OPTIONS.map(function (opt) {
            var isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                onClick={function () { handleSelect(opt); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '6px 10px', border: 'none', cursor: 'pointer',
                  fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 500,
                  color: opt.color,
                  background: isSelected ? opt.bg : 'transparent',
                  borderRadius: '4px', transition: 'background 0.1s'
                }}
                onMouseEnter={function (e) { e.currentTarget.style.background = opt.bg; }}
                onMouseLeave={function (e) {
                  e.currentTarget.style.background = isSelected ? opt.bg : 'transparent';
                }}
              >
                <span style={{
                  display: 'inline-block', width: '8px', height: '8px',
                  borderRadius: '50%', background: opt.color, marginRight: '8px',
                  verticalAlign: 'middle'
                }} />
                {opt.value}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SORT / FILTER CONSTANTS & HELPERS
// ══════════════════════════════════════════════════════════════════════════

var SORTABLE_COLUMNS = [
  { key: 'task', label: 'Task' },
  { key: 'categories', label: 'Category' },
  { key: 'assignedUserIds', label: 'Assigned To' },
  { key: 'priority', label: 'Priority' },
  { key: 'startDate', label: 'Start' },
  { key: 'completionDate', label: 'Due' },
  { key: 'progress', label: 'Progress' }
];

var FILTERABLE_COLUMNS = [
  { key: 'categories', label: 'Category', type: 'multi' },
  { key: 'assignedUserIds', label: 'Assigned To', type: 'multi' },
  { key: 'priority', label: 'Priority', type: 'single' },
  { key: 'progress', label: 'Progress', type: 'status' },
  { key: 'completionDate', label: 'Due', type: 'date' },
  { key: 'startDate', label: 'Start', type: 'date' }
];

var FILTER_CONDITIONS = {
  multi: ['is', 'is not', 'contains'],
  single: ['is', 'is not'],
  status: ['is', 'is not'],
  date: ['is', 'is before', 'is after', 'is empty', 'is not empty']
};

// ── Sort Panel ──────────────────────────────────────────────────────────

function SortPanel({ sortRules: initialRules, onChangeSortRules, onSaveForEveryone, onSaveForMe, onClose, canSaveForEveryone }) {
  var [draft, setDraft] = useState(function () { return (initialRules || []).slice(); });
  var panelRef = useRef(null);

  useEffect(function () {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); };
  }, [onClose]);

  // Apply newDraft both locally (for UI) and to the parent (live apply + auto-save)
  var applyDraft = function (newDraft) {
    setDraft(newDraft);
    onChangeSortRules(newDraft);
  };

  var addRule = function () {
    var usedKeys = draft.map(function (r) { return r.key; });
    var available = SORTABLE_COLUMNS.filter(function (c) { return usedKeys.indexOf(c.key) === -1; });
    if (available.length === 0) return;
    applyDraft(draft.concat({ key: available[0].key, dir: 'asc' }));
  };

  var removeRule = function (idx) {
    applyDraft(draft.filter(function (_, i) { return i !== idx; }));
  };

  var updateRule = function (idx, field, value) {
    var next = draft.map(function (r, i) {
      if (i !== idx) return r;
      var copy = { key: r.key, dir: r.dir };
      copy[field] = value;
      return copy;
    });
    applyDraft(next);
  };

  var moveRule = function (idx, direction) {
    var next = draft.slice();
    var targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    var temp = next[idx];
    next[idx] = next[targetIdx];
    next[targetIdx] = temp;
    applyDraft(next);
  };

  var usedKeys = draft.map(function (r) { return r.key; });

  return (
    <div ref={panelRef} style={{
      position: 'absolute', top: '100%', right: 0, zIndex: 200,
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      minWidth: '380px', marginTop: '4px', padding: '12px'
    }}>
      <div style={{ fontSize: '11px', fontWeight: 500, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
        Sort rules
      </div>

      {draft.length === 0 && (
        <div style={{ fontSize: '12px', fontWeight: 300, color: '#aaa', fontStyle: 'italic', padding: '8px 0' }}>
          No sorting applied
        </div>
      )}

      {draft.map(function (rule, idx) {
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            {/* Reorder arrows */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
              <button
                onClick={function () { moveRule(idx, -1); }}
                disabled={idx === 0}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '18px', height: '14px', border: 'none', background: 'transparent',
                  color: idx === 0 ? '#ddd' : '#999', cursor: idx === 0 ? 'default' : 'pointer', padding: 0
                }}
              >
                <ChevronUp size={10} />
              </button>
              <button
                onClick={function () { moveRule(idx, 1); }}
                disabled={idx === draft.length - 1}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '18px', height: '14px', border: 'none', background: 'transparent',
                  color: idx === draft.length - 1 ? '#ddd' : '#999',
                  cursor: idx === draft.length - 1 ? 'default' : 'pointer', padding: 0
                }}
              >
                <ChevronDown size={10} />
              </button>
            </div>

            {/* Column selector */}
            <select
              value={rule.key}
              onChange={function (e) { updateRule(idx, 'key', e.target.value); }}
              style={{
                fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
                padding: '4px 8px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
                background: '#fff', color: '#333', cursor: 'pointer', flex: 1
              }}
            >
              {SORTABLE_COLUMNS.map(function (col) {
                var disabled = usedKeys.indexOf(col.key) !== -1 && col.key !== rule.key;
                return <option key={col.key} value={col.key} disabled={disabled}>{col.label}</option>;
              })}
            </select>

            {/* Direction selector */}
            <select
              value={rule.dir}
              onChange={function (e) { updateRule(idx, 'dir', e.target.value); }}
              style={{
                fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
                padding: '4px 8px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
                background: '#fff', color: '#333', cursor: 'pointer', width: '110px'
              }}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>

            {/* Remove */}
            <button
              onClick={function () { removeRule(idx); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '24px', height: '24px', border: 'none', background: 'transparent',
                color: '#bbb', cursor: 'pointer', padding: 0, borderRadius: '3px'
              }}
              onMouseEnter={function (e) { e.currentTarget.style.color = BRIO_RED; e.currentTarget.style.background = '#93494810'; }}
              onMouseLeave={function (e) { e.currentTarget.style.color = '#bbb'; e.currentTarget.style.background = 'transparent'; }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}

      <button
        onClick={addRule}
        disabled={usedKeys.length >= SORTABLE_COLUMNS.length}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400,
          padding: '4px 8px', border: 'none', background: 'transparent',
          color: usedKeys.length >= SORTABLE_COLUMNS.length ? '#ccc' : BRIO_GOLD,
          cursor: usedKeys.length >= SORTABLE_COLUMNS.length ? 'default' : 'pointer',
          marginTop: '4px'
        }}
      >
        <Plus size={12} /> Add sort
      </button>

      {/* Save buttons — switch which DB key the rules are stored under */}
      <div style={{ borderTop: '1px solid ' + BRIO_BORDER, marginTop: '10px', paddingTop: '10px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        <button
          onClick={function () { onSaveForMe(); onClose(); }}
          style={{
            fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400,
            padding: '5px 12px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
            background: '#fff', color: BRIO_GREY, cursor: 'pointer'
          }}
          onMouseEnter={function (e) { e.currentTarget.style.borderColor = BRIO_GREY; }}
          onMouseLeave={function (e) { e.currentTarget.style.borderColor = BRIO_BORDER; }}
        >
          Save for me
        </button>
        {canSaveForEveryone && (
          <button
            onClick={function () { onSaveForEveryone(); onClose(); }}
            style={{
              fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 500,
              padding: '5px 12px', border: '1px solid ' + BRIO_GOLD, borderRadius: '3px',
              background: BRIO_GOLD, color: '#fff', cursor: 'pointer'
            }}
            onMouseEnter={function (e) { e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={function (e) { e.currentTarget.style.opacity = '1'; }}
          >
            Save for everyone
          </button>
        )}
      </div>
    </div>
  );
}

// ── Filter Panel ────────────────────────────────────────────────────────

function FilterPanel({ filterRules: initialRules, onChangeFilterRules, onSaveForEveryone, onSaveForMe, onClose, priorityOptions, getUserName, allUsers, taskCategories: taskCatsOverride, canSaveForEveryone }) {
  var TASK_CATEGORIES = taskCatsOverride || ['Follow up','BRIO','Client','Structure','MEP','Landscape','Consultants','Vendors','Site coordination','PMC','MOM_Linked'];
  var [draft, setDraft] = useState(function () { return (initialRules || []).slice(); });
  var panelRef = useRef(null);

  useEffect(function () {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); };
  }, [onClose]);

  // Apply newDraft both locally (for UI) and to the parent (live apply + auto-save)
  var applyDraft = function (newDraft) {
    setDraft(newDraft);
    onChangeFilterRules(newDraft);
  };

  var addFilter = function () {
    applyDraft(draft.concat({ key: FILTERABLE_COLUMNS[0].key, condition: 'is', value: '' }));
  };

  var removeFilter = function (idx) {
    applyDraft(draft.filter(function (_, i) { return i !== idx; }));
  };

  var updateFilter = function (idx, field, val) {
    var next = draft.map(function (r, i) {
      if (i !== idx) return r;
      var copy = { key: r.key, condition: r.condition, value: r.value };
      copy[field] = val;
      return copy;
    });
    // Reset condition and value when column changes
    if (field === 'key') {
      var colDef = FILTERABLE_COLUMNS.find(function (c) { return c.key === val; });
      var conditions = FILTER_CONDITIONS[colDef ? colDef.type : 'single'];
      next[idx].condition = conditions[0];
      next[idx].value = '';
    }
    applyDraft(next);
  };

  var getValueOptions = function (colKey) {
    var colDef = FILTERABLE_COLUMNS.find(function (c) { return c.key === colKey; });
    if (!colDef) return [];
    if (colKey === 'categories') return TASK_CATEGORIES;
    if (colKey === 'priority') return (priorityOptions || []).map(function (p) { return p.value; });
    if (colKey === 'progress') return ['Not started', 'In progress', 'Done'];
    if (colKey === 'assignedUserIds') {
      return (allUsers || []).map(function (u) { return { id: String(u.id), name: u.name }; });
    }
    return [];
  };

  return (
    <div ref={panelRef} style={{
      position: 'absolute', top: '100%', right: 0, zIndex: 200,
      background: '#fff', border: '1px solid ' + BRIO_BORDER,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      minWidth: '460px', marginTop: '4px', padding: '12px'
    }}>
      <div style={{ fontSize: '11px', fontWeight: 500, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
        Filter rules
      </div>

      {draft.length === 0 && (
        <div style={{ fontSize: '12px', fontWeight: 300, color: '#aaa', fontStyle: 'italic', padding: '8px 0' }}>
          No filters applied
        </div>
      )}

      {draft.map(function (rule, idx) {
        var colDef = FILTERABLE_COLUMNS.find(function (c) { return c.key === rule.key; });
        var colType = colDef ? colDef.type : 'single';
        var conditions = FILTER_CONDITIONS[colType] || FILTER_CONDITIONS.single;
        var needsValue = rule.condition !== 'is empty' && rule.condition !== 'is not empty';
        var valueOptions = getValueOptions(rule.key);

        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            {/* Column */}
            <select
              value={rule.key}
              onChange={function (e) { updateFilter(idx, 'key', e.target.value); }}
              style={{
                fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
                padding: '4px 8px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
                background: '#fff', color: '#333', cursor: 'pointer', width: '120px'
              }}
            >
              {FILTERABLE_COLUMNS.map(function (col) {
                return <option key={col.key} value={col.key}>{col.label}</option>;
              })}
            </select>

            {/* Condition */}
            <select
              value={rule.condition}
              onChange={function (e) { updateFilter(idx, 'condition', e.target.value); }}
              style={{
                fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
                padding: '4px 8px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
                background: '#fff', color: '#333', cursor: 'pointer', width: '110px'
              }}
            >
              {conditions.map(function (c) {
                return <option key={c} value={c}>{c}</option>;
              })}
            </select>

            {/* Value */}
            {needsValue && (
              rule.key === 'assignedUserIds' ? (
                <select
                  value={rule.value}
                  onChange={function (e) { updateFilter(idx, 'value', e.target.value); }}
                  style={{
                    fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
                    padding: '4px 8px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
                    background: '#fff', color: '#333', cursor: 'pointer', flex: 1
                  }}
                >
                  <option value="">Select…</option>
                  {valueOptions.map(function (u) {
                    return <option key={u.id} value={u.id}>{u.name}</option>;
                  })}
                </select>
              ) : colType === 'date' ? (
                <input
                  type="date"
                  value={rule.value || ''}
                  onChange={function (e) { updateFilter(idx, 'value', e.target.value); }}
                  style={{
                    fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
                    padding: '4px 8px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
                    background: '#fff', color: '#333', flex: 1
                  }}
                />
              ) : (
                <select
                  value={rule.value}
                  onChange={function (e) { updateFilter(idx, 'value', e.target.value); }}
                  style={{
                    fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 300,
                    padding: '4px 8px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
                    background: '#fff', color: '#333', cursor: 'pointer', flex: 1
                  }}
                >
                  <option value="">Select…</option>
                  {valueOptions.map(function (v) {
                    return <option key={v} value={v}>{v}</option>;
                  })}
                </select>
              )
            )}

            {/* Remove */}
            <button
              onClick={function () { removeFilter(idx); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '24px', height: '24px', border: 'none', background: 'transparent',
                color: '#bbb', cursor: 'pointer', padding: 0, borderRadius: '3px'
              }}
              onMouseEnter={function (e) { e.currentTarget.style.color = BRIO_RED; e.currentTarget.style.background = '#93494810'; }}
              onMouseLeave={function (e) { e.currentTarget.style.color = '#bbb'; e.currentTarget.style.background = 'transparent'; }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}

      <button
        onClick={addFilter}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400,
          padding: '4px 8px', border: 'none', background: 'transparent',
          color: BRIO_GOLD, cursor: 'pointer', marginTop: '4px'
        }}
      >
        <Plus size={12} /> Add filter
      </button>

      {/* Save buttons */}
      <div style={{ borderTop: '1px solid ' + BRIO_BORDER, marginTop: '10px', paddingTop: '10px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        <button
          onClick={function () { onSaveForMe(); onClose(); }}
          style={{
            fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400,
            padding: '5px 12px', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
            background: '#fff', color: BRIO_GREY, cursor: 'pointer'
          }}
          onMouseEnter={function (e) { e.currentTarget.style.borderColor = BRIO_GREY; }}
          onMouseLeave={function (e) { e.currentTarget.style.borderColor = BRIO_BORDER; }}
        >
          Save for me
        </button>
        {canSaveForEveryone && (
          <button
            onClick={function () { onSaveForEveryone(); onClose(); }}
            style={{
              fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 500,
              padding: '5px 12px', border: '1px solid ' + BRIO_GOLD, borderRadius: '3px',
              background: BRIO_GOLD, color: '#fff', cursor: 'pointer'
            }}
            onMouseEnter={function (e) { e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={function (e) { e.currentTarget.style.opacity = '1'; }}
          >
            Save for everyone
          </button>
        )}
      </div>
    </div>
  );
}

// ── Filter Chips ────────────────────────────────────────────────────────

function FilterChips({ filterRules, onRemove, getUserName }) {
  if (!filterRules || filterRules.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
      {filterRules.map(function (rule, idx) {
        var colDef = FILTERABLE_COLUMNS.find(function (c) { return c.key === rule.key; });
        var colLabel = colDef ? colDef.label : rule.key;
        var displayValue = rule.value;
        if (rule.key === 'assignedUserIds' && rule.value) {
          displayValue = getUserName(rule.value);
        }
        if (rule.condition === 'is empty' || rule.condition === 'is not empty') {
          displayValue = '';
        }
        var chipText = colLabel + ' ' + rule.condition + (displayValue ? ' ' + displayValue : '');

        return (
          <span key={idx} style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            fontSize: '11px', fontWeight: 400, padding: '3px 8px 3px 10px',
            background: BRIO_GOLD + '12', color: BRIO_GOLD,
            border: '1px solid ' + BRIO_GOLD + '25', borderRadius: '3px',
            fontFamily: 'Raleway, sans-serif', lineHeight: '16px'
          }}>
            {chipText}
            <button
              onClick={function () { onRemove(idx); }}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '14px', height: '14px', border: 'none', background: 'transparent',
                color: BRIO_GOLD, cursor: 'pointer', padding: 0, borderRadius: '2px'
              }}
              onMouseEnter={function (e) { e.currentTarget.style.background = BRIO_GOLD + '20'; }}
              onMouseLeave={function (e) { e.currentTarget.style.background = 'transparent'; }}
            >
              <X size={10} />
            </button>
          </span>
        );
      })}
    </div>
  );
}

// ── Apply sort & filter logic ───────────────────────────────────────────

function matchDateFilter(task, rule) {
  var taskDate = task[rule.key] || '';
  if (rule.condition === 'is empty') return !taskDate;
  if (rule.condition === 'is not empty') return !!taskDate;
  if (!rule.value) return true;
  if (rule.condition === 'is') return taskDate === rule.value;
  if (rule.condition === 'is before') return !!taskDate && taskDate < rule.value;
  if (rule.condition === 'is after') return !!taskDate && taskDate > rule.value;
  return true;
}

function matchStatusFilter(task, rule) {
  if (!rule.value) return true;
  var p = task.progress;
  var statusLabel = (p >= 100) ? 'Done' : (p > 0 ? 'In progress' : 'Not started');
  if (rule.condition === 'is') return statusLabel === rule.value;
  if (rule.condition === 'is not') return statusLabel !== rule.value;
  return true;
}

function matchMultiFilter(task, rule, getUserName) {
  if (!rule.value) return true;
  var raw = task[rule.key];
  var arr = Array.isArray(raw) ? raw.map(String) : [];
  var matchVal = String(rule.value);
  if (rule.condition === 'is') return arr.indexOf(matchVal) !== -1;
  if (rule.condition === 'is not') return arr.indexOf(matchVal) === -1;
  if (rule.condition === 'contains') {
    if (rule.key === 'assignedUserIds') {
      var searchName = (getUserName(matchVal) || '').toLowerCase();
      return arr.some(function (uid) {
        return (getUserName(uid) || '').toLowerCase().indexOf(searchName) !== -1;
      });
    }
    return arr.some(function (v) { return v.toLowerCase().indexOf(matchVal.toLowerCase()) !== -1; });
  }
  return true;
}

function matchSingleFilter(task, rule) {
  if (!rule.value) return true;
  var taskVal = task[rule.key] || '';
  if (rule.condition === 'is') return taskVal === rule.value;
  if (rule.condition === 'is not') return taskVal !== rule.value;
  return true;
}

function applyFilters(tasks, filterRules, getUserName) {
  if (!filterRules || filterRules.length === 0) return tasks;

  return tasks.filter(function (task) {
    return filterRules.every(function (rule) {
      var colDef = FILTERABLE_COLUMNS.find(function (c) { return c.key === rule.key; });
      if (!colDef) return true;

      if (colDef.type === 'date') return matchDateFilter(task, rule);
      if (colDef.type === 'status') return matchStatusFilter(task, rule);
      if (colDef.type === 'multi') return matchMultiFilter(task, rule, getUserName);
      if (colDef.type === 'single') return matchSingleFilter(task, rule);
      return true;
    });
  });
}

var ROLE_ORDER = { 'admin': 0, 'admin-pa': 0, 'supervisor': 1, 'user': 2 };

function applySorts(tasks, sortRules, getUserName, priorityOptions, taskCategories, allUsers) {
  if (!sortRules || sortRules.length === 0) {
    return tasks.slice().sort(function (a, b) { return (a.task || '').localeCompare(b.task || ''); });
  }

  var priorityOrder = {};
  (priorityOptions || []).forEach(function (p, i) { priorityOrder[p.value] = i; });

  var userRoleMap = {};
  (allUsers || []).forEach(function (u) { userRoleMap[String(u.id)] = u.role || 'user'; });

  var catIndex = {};
  (taskCategories || []).forEach(function (c, i) {
    var name = typeof c === 'string' ? c : c.name;
    catIndex[name] = i;
  });

  function firstCatIdx(cats) {
    if (!Array.isArray(cats) || cats.length === 0) return 9999;
    var first = cats[0];
    return (typeof first === 'string' && first in catIndex) ? catIndex[first] : 9999;
  }

  function minRoleOrder(ids) {
    if (!Array.isArray(ids) || ids.length === 0) return 999;
    return Math.min.apply(null, ids.map(function (id) {
      var role = userRoleMap[String(id)] || 'user';
      return ROLE_ORDER[role] !== undefined ? ROLE_ORDER[role] : 2;
    }));
  }

  return tasks.slice().sort(function (a, b) {
    for (var i = 0; i < sortRules.length; i++) {
      var rule = sortRules[i];
      var dir = rule.dir === 'desc' ? -1 : 1;
      var cmp = 0;

      if (rule.key === 'task') {
        cmp = (a.task || '').localeCompare(b.task || '');
      } else if (rule.key === 'categories') {
        var aIdx = firstCatIdx(a.categories);
        var bIdx = firstCatIdx(b.categories);
        // Uncategorised tasks always sink to the end regardless of sort direction
        if (aIdx === 9999 && bIdx !== 9999) return 1;
        if (bIdx === 9999 && aIdx !== 9999) return -1;
        cmp = aIdx - bIdx;
      } else if (rule.key === 'assignedUserIds') {
        cmp = minRoleOrder(a.assignedUserIds) - minRoleOrder(b.assignedUserIds);
        if (cmp === 0) {
          var aName = Array.isArray(a.assignedUserIds) ? a.assignedUserIds.map(function (id) { return getUserName(id); }).join(', ') : '';
          var bName = Array.isArray(b.assignedUserIds) ? b.assignedUserIds.map(function (id) { return getUserName(id); }).join(', ') : '';
          cmp = aName.localeCompare(bName);
        }
      } else if (rule.key === 'priority') {
        var aP = a.priority in priorityOrder ? priorityOrder[a.priority] : 999;
        var bP = b.priority in priorityOrder ? priorityOrder[b.priority] : 999;
        cmp = aP - bP;
      } else if (rule.key === 'startDate' || rule.key === 'completionDate') {
        var aD = a[rule.key] || '';
        var bD = b[rule.key] || '';
        if (!aD && !bD) cmp = 0;
        else if (!aD) cmp = 1;
        else if (!bD) cmp = -1;
        else cmp = aD.localeCompare(bD);
      } else if (rule.key === 'progress') {
        cmp = (a.progress || 0) - (b.progress || 0);
      }

      if (cmp !== 0) return cmp * dir;
    }
    // Alphabetical by task name as final tiebreaker
    return (a.task || '').localeCompare(b.task || '');
  });
}

// ══════════════════════════════════════════════════════════════════════════
// TOOLBAR ICON BUTTON (with tooltip above)
// ══════════════════════════════════════════════════════════════════════════

function ToolbarIcon({ onClick, disabled, tooltip, active, badge, frameless, children }) {
  var [hovered, setHovered] = useState(false);
  var hasBorder = !frameless;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px',
        padding: '4px 6px',
        border: hasBorder ? '1px solid ' + (active ? BRIO_GOLD : (hovered ? BRIO_GOLD : BRIO_BORDER)) : 'none',
        borderRadius: hasBorder ? '4px' : '0', background: 'transparent',
        color: active ? BRIO_GOLD : (hovered ? BRIO_GOLD : '#888'),
        cursor: disabled ? 'wait' : 'pointer',
        transition: 'all 0.15s ease', position: 'relative',
        fontFamily: 'Raleway, sans-serif', fontSize: '10px', fontWeight: 600, lineHeight: 1
      }}
      onMouseEnter={function () { setHovered(true); }}
      onMouseLeave={function () { setHovered(false); }}
    >
      {children}
      {badge != null && <span style={{ fontSize: '9px', fontWeight: 500 }}>({badge})</span>}
      {hovered && tooltip && (
        <span style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: '6px', padding: '3px 8px', borderRadius: '3px',
          background: '#333', color: '#fff', fontSize: '10px', fontWeight: 400,
          whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 300,
          lineHeight: '14px'
        }}>
          {tooltip}
        </span>
      )}
    </button>
  );
}

// Zoom % label — double-click resets to 100%, hover shows "double click to reset" tooltip
function ZoomResetLabel({ zoomLabel, onReset }) {
  var [hovered, setHovered] = useState(false);
  return (
    <span
      onDoubleClick={onReset}
      onMouseEnter={function () { setHovered(true); }}
      onMouseLeave={function () { setHovered(false); }}
      style={{ position: 'relative', fontSize: '10px', fontWeight: 400, color: '#555', fontFamily: 'Raleway, sans-serif', minWidth: '28px', textAlign: 'center', userSelect: 'none', cursor: 'default', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {zoomLabel}
      {hovered && (
        <span style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: '50%', transform: 'translateX(-50%)', padding: '3px 8px', background: '#333', color: '#fff', fontSize: '10px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 500, borderRadius: '3px' }}>
          double click to reset
        </span>
      )}
    </span>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// STATUS TRACKER BOARD (Kanban view)
// ══════════════════════════════════════════════════════════════════════════

var BOARD_COLUMNS = [
  { id: 'overdue', label: 'Overdue', progressValue: null, color: BRIO_DEEP_RED, bg: '#6B202012', border: BRIO_DEEP_RED + '30' },
  { id: 'inprogress', label: 'In progress', progressValue: 50, color: '#9F902A', bg: 'rgba(159, 144, 42, 0.1)', border: '#9F902A30' },
  { id: 'notstarted', label: 'Not started', progressValue: 0, color: '#999', bg: '#f0f0f0', border: '#99999930' },
  { id: 'done', label: 'Done', progressValue: 100, color: BRIO_GREEN, bg: '#68958115', border: BRIO_GREEN + '30' }
];

function getTaskBoardColumn(task) {
  if (task.progress >= 100) return 'done';
  if (isOverdueCheck(task.completionDate, task.progress)) return 'overdue';
  if (task.progress > 0 && task.progress < 100) return 'inprogress';
  return 'notstarted';
}

// ── Draggable Task Card ───────────────────────────────────────────────

function TaskCard({ task, getUserName, getUserColor, isDragging, assignedTagMinWidth, isOverdue, onUpdateTask, canEdit }) {
  var { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'task-' + task.id,
    data: { task: task }
  });

  var [editing, setEditing] = useState(false);
  var [localName, setLocalName] = useState(task.task || '');
  useEffect(function () { if (!editing) setLocalName(task.task || ''); }, [task.task, editing]);

  var assignedIds = Array.isArray(task.assignedUserIds) ? task.assignedUserIds.map(String) : [];

  // Background based on progress column
  var cardBg;
  if (isOverdue) cardBg = 'rgba(107, 32, 32, 0.12)';
  else if (task.progress >= 100) cardBg = 'rgba(66, 187, 120, 0.14)';
  else if (task.progress > 0) cardBg = 'rgba(149, 137, 52, 0.14)';
  else cardBg = '#eeede8';

  var cardStyle = {
    background: cardBg,
    border: '1px solid ' + BRIO_BORDER,
    borderRadius: '4px',
    padding: '10px 12px',
    marginBottom: '6px',
    transition: 'box-shadow 0.15s ease, opacity 0.15s ease',
    opacity: isDragging ? 0.4 : 1,
    fontFamily: 'Raleway, sans-serif',
    position: 'relative'
  };

  if (transform) {
    cardStyle.transform = 'translate(' + transform.x + 'px, ' + transform.y + 'px)';
    cardStyle.zIndex = 500;
    cardStyle.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
  }

  var commitName = function () {
    setEditing(false);
    var trimmed = localName.trim();
    if (trimmed !== (task.task || '').trim() && onUpdateTask) {
      onUpdateTask(task.id, { task: trimmed });
    }
  };

  return (
    <div ref={setNodeRef} style={cardStyle}>
      {/* Drag handle — only this area initiates dragging */}
      {canEdit && (
        <div
          {...listeners} {...attributes}
          style={{
            position: 'absolute', top: '6px', right: '6px',
            cursor: isDragging ? 'grabbing' : 'grab',
            color: '#ccc', display: 'flex', alignItems: 'center', padding: '2px',
            touchAction: 'none',
            userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
          }}
        >
          <GripVertical size={12} />
        </div>
      )}

      {/* Task name — click to edit; drag handle above handles dragging */}
      {editing ? (
        <textarea
          autoFocus
          value={localName}
          onChange={function (e) { setLocalName(e.target.value); }}
          onBlur={commitName}
          onKeyDown={function (e) {
            if (e.key === 'Escape') { setLocalName(task.task || ''); setEditing(false); }
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commitName(); }
          }}
          style={{
            width: '100%', resize: 'none', border: '1px solid ' + BRIO_GOLD,
            borderRadius: '2px', padding: '2px 4px', fontSize: '12px', fontWeight: 400,
            fontFamily: 'Raleway, sans-serif', lineHeight: '1.4', color: '#333',
            background: 'transparent', outline: 'none', marginBottom: assignedIds.length > 0 ? '8px' : 0,
            minHeight: '32px', paddingRight: canEdit ? '20px' : undefined
          }}
          rows={2}
        />
      ) : (
        <div
          onClick={function () { if (canEdit) setEditing(true); }}
          style={{
            fontSize: '12px', fontWeight: 400, color: '#333', lineHeight: '1.4',
            marginBottom: assignedIds.length > 0 ? '8px' : 0,
            wordBreak: 'break-word',
            textDecoration: task.progress >= 100 ? 'line-through' : 'none',
            cursor: canEdit ? 'text' : 'default',
            paddingRight: canEdit ? '18px' : undefined
          }}
        >
          {task.task || 'Untitled'}
        </div>
      )}

      {/* Assigned To tags */}
      {assignedIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
          {assignedIds.map(function (uid) {
            var uc = getUserColor ? getUserColor(uid) : BRIO_GOLD;
            return (
              <span key={uid} style={{
                fontSize: '9px', fontWeight: 400, padding: '1px 5px',
                background: uc + '15', color: uc,
                border: '1px solid ' + uc + '30', borderRadius: '2px',
                whiteSpace: 'nowrap', lineHeight: '14px',
                minWidth: assignedTagMinWidth ? (assignedTagMinWidth + 'px') : undefined,
                display: 'inline-block', textAlign: 'left'
              }}>
                {getUserName(uid)}
              </span>
            );
          })}
        </div>
      )}

      {/* Due date — only shown for overdue tasks */}
      {isOverdue && task.completionDate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
          <span style={{
            fontSize: '9px', fontWeight: 300, color: BRIO_DEEP_RED,
            display: 'inline-flex', alignItems: 'center', gap: '3px'
          }}>
            <AlertTriangle size={9} strokeWidth={1.8} />
            {new Date(task.completionDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Drag Overlay Card (ghost while dragging) ──────────────────────────

function TaskCardOverlay({ task, getUserName, getUserColor }) {
  var assignedIds = Array.isArray(task.assignedUserIds) ? task.assignedUserIds.map(String) : [];

  return (
    <div style={{
      background: 'rgba(159, 144, 42, 0.09)',
      border: '1px solid ' + BRIO_GOLD,
      borderRadius: '4px',
      padding: '10px 12px',
      boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
      fontFamily: 'Raleway, sans-serif',
      width: '240px',
      cursor: 'grabbing'
    }}>
      <div style={{
        fontSize: '12px', fontWeight: 400, color: '#333', lineHeight: '1.4',
        marginBottom: assignedIds.length > 0 ? '8px' : 0,
        wordBreak: 'break-word'
      }}>
        {task.task || 'Untitled'}
      </div>
      {assignedIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
          {assignedIds.map(function (uid) {
            var uc = getUserColor ? getUserColor(uid) : BRIO_GOLD;
            return (
              <span key={uid} style={{
                fontSize: '9px', fontWeight: 400, padding: '1px 5px',
                background: uc + '15', color: uc,
                border: '1px solid ' + uc + '30', borderRadius: '2px',
                whiteSpace: 'nowrap', lineHeight: '14px'
              }}>{getUserName(uid)}</span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Droppable Status Column (flat / no-groups mode) ───────────────────

function StatusColumn({ column, tasks, getUserName, getUserColor, activeId, onAddTask, onUpdateTask, canEdit }) {
  var { setNodeRef, isOver } = useDroppable({
    id: 'column-' + column.id
  });

  var assignedTagMinWidth = useMemo(function () {
    var charW = 5; var pad = 12; var maxLen = 0;
    tasks.forEach(function (t) {
      (t.assignedUserIds || []).forEach(function (uid) {
        var name = getUserName(uid);
        if (name.length > maxLen) maxLen = name.length;
      });
    });
    return maxLen > 0 ? Math.ceil(maxLen * charW + pad) : 0;
  }, [tasks, getUserName]);

  return (
    <div ref={setNodeRef} style={{ flex: 1, minWidth: '220px', maxWidth: '320px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', marginBottom: '8px' }}>
        <span style={{
          fontSize: '10px', fontWeight: 400, padding: '2px 8px',
          background: column.bg, color: column.color,
          border: '1px solid ' + column.border, borderRadius: '2px',
          whiteSpace: 'nowrap', lineHeight: '14px'
        }}>{column.label}</span>
        <span style={{ fontSize: '11px', fontWeight: 300, color: '#aaa' }}>{tasks.length}</span>
      </div>
      <div style={{
        flex: 1, padding: '0 6px 6px', minHeight: '80px',
        background: isOver ? BRIO_GOLD + '08' : 'transparent', borderRadius: '4px',
        transition: 'background 0.15s ease',
        border: isOver ? '1px dashed ' + BRIO_GOLD + '40' : '1px dashed transparent'
      }}>
        {tasks.map(function (task) {
          return (
            <TaskCard key={task.id} task={task} getUserName={getUserName} getUserColor={getUserColor}
              isDragging={activeId === 'task-' + task.id} assignedTagMinWidth={assignedTagMinWidth}
              isOverdue={column.id === 'overdue'} onUpdateTask={onUpdateTask} canEdit={canEdit} />
          );
        })}
        {canEdit && (
          <button onClick={function () { onAddTask(column); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              width: '100%', padding: '8px 10px', border: 'none', background: 'transparent',
              color: '#bbb', fontSize: '11px', fontWeight: 300,
              fontFamily: 'Raleway, sans-serif', cursor: 'pointer', borderRadius: '4px',
              transition: 'color 0.15s, background 0.15s'
            }}
            onMouseEnter={function (e) { e.currentTarget.style.color = BRIO_GOLD; e.currentTarget.style.background = BRIO_GOLD + '08'; }}
            onMouseLeave={function (e) { e.currentTarget.style.color = '#bbb'; e.currentTarget.style.background = 'transparent'; }}
          ><Plus size={12} />Add task</button>
        )}
      </div>
    </div>
  );
}

// ── Droppable cell in grouped status board (one per group × column) ───

function GroupedStatusCell({ column, groupId, tasks, getUserName, getUserColor, activeId, onUpdateTask, canEdit }) {
  var dropId = groupId ? 'cell-' + column.id + '-' + groupId : 'cell-' + column.id + '-ungrouped';
  var { setNodeRef, isOver } = useDroppable({
    id: dropId,
    data: { type: 'status-cell', columnId: column.id, groupId: groupId || null }
  });

  var assignedTagMinWidth = useMemo(function () {
    var charW = 5; var pad = 12; var maxLen = 0;
    tasks.forEach(function (t) {
      (t.assignedUserIds || []).forEach(function (uid) {
        var name = getUserName(uid);
        if (name.length > maxLen) maxLen = name.length;
      });
    });
    return maxLen > 0 ? Math.ceil(maxLen * charW + pad) : 0;
  }, [tasks, getUserName]);

  return (
    <div ref={setNodeRef} style={{
      flex: 1, minWidth: 0, padding: '4px', minHeight: '40px',
      background: isOver ? BRIO_GOLD + '08' : 'transparent', borderRadius: '4px',
      transition: 'background 0.15s ease',
      border: isOver ? '1px dashed ' + BRIO_GOLD + '40' : '1px dashed transparent'
    }}>
      {tasks.map(function (task) {
        return (
          <TaskCard key={task.id} task={task} getUserName={getUserName} getUserColor={getUserColor}
            isDragging={activeId === 'task-' + task.id} assignedTagMinWidth={assignedTagMinWidth}
            isOverdue={column.id === 'overdue'} onUpdateTask={onUpdateTask} canEdit={canEdit} />
        );
      })}
    </div>
  );
}

// ── Droppable ungrouped header in status tracker ──────────────────────

function UngroupedHeaderDropZone({ taskCount }) {
  var { setNodeRef, isOver } = useDroppable({
    id: 'board-ungrouped-drop',
    data: { type: 'board-group-drop', groupId: null }
  });
  return (
    <div ref={setNodeRef} style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '4px 10px', marginBottom: '2px', borderRadius: '4px',
      background: isOver ? '#99999920' : 'transparent',
      transition: 'background 0.15s ease', cursor: 'default'
    }}>
      <span style={{ fontSize: '10px', fontWeight: 400, color: '#999', fontFamily: 'Raleway, sans-serif' }}>
        Ungrouped
      </span>
      <span style={{ fontSize: '10px', fontWeight: 300, color: '#ccc' }}>({taskCount})</span>
    </div>
  );
}

// ── Droppable group header row in status tracker ──────────────────────

function GroupHeaderDropZone({ group, isCollapsed, onToggleCollapse, taskCount }) {
  var { setNodeRef, isOver } = useDroppable({
    id: 'board-group-drop-' + group.id,
    data: { type: 'board-group-drop', groupId: group.id }
  });

  var groupColor = group.color || BRIO_GOLD;

  return (
    <div
      ref={setNodeRef}
      onClick={function () { onToggleCollapse && onToggleCollapse(group.id); }}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '5px 10px', cursor: 'pointer', userSelect: 'none',
        borderLeft: '3px solid ' + groupColor,
        background: isOver ? groupColor + '18' : groupColor + '08',
        borderRadius: '0 4px 4px 0', marginBottom: '2px',
        transition: 'background 0.15s ease'
      }}
    >
      <ChevronRight size={10} style={{
        color: groupColor, flexShrink: 0,
        transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
        transition: 'transform 0.15s ease'
      }} />
      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: groupColor, flexShrink: 0 }} />
      <span style={{ fontSize: '11px', fontWeight: 500, color: groupColor, fontFamily: 'Raleway, sans-serif', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {group.name}
      </span>
      <span style={{ fontSize: '10px', fontWeight: 300, color: '#aaa' }}>({taskCount})</span>
    </div>
  );
}

// ── Status Tracker Board ──────────────────────────────────────────────

function StatusTrackerBoard({ tasks, groupedDisplaySections, collapsedGroupIds, onToggleGroupCollapse, getUserName, getUserColor, onUpdateProgress, onUpdateTask, onAddTask, onMoveTaskToGroup, canEdit }) {
  var [activeId, setActiveId] = useState(null);
  var [activeTask, setActiveTask] = useState(null);

  var sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  var hasGroups = groupedDisplaySections && groupedDisplaySections.length > 1;

  // Group tasks into columns (flat mode)
  var columnTasks = useMemo(function () {
    var groups = { inprogress: [], overdue: [], done: [], notstarted: [] };
    (tasks || []).forEach(function (task) {
      var col = getTaskBoardColumn(task);
      if (groups[col]) groups[col].push(task);
    });
    return groups;
  }, [tasks]);

  var handleDragStart = function (event) {
    setActiveId(event.active.id);
    setActiveTask(event.active.data.current ? event.active.data.current.task : null);
  };

  var handleDragEnd = function (event) {
    setActiveId(null);
    setActiveTask(null);

    var over = event.over;
    if (!over) return;
    var task = event.active.data.current ? event.active.data.current.task : null;
    if (!task) return;

    var overData = over.data.current || {};

    // Drop on a group header → move task to that group
    if (overData.type === 'board-group-drop' && onMoveTaskToGroup) {
      onMoveTaskToGroup(task.id, overData.groupId);
      return;
    }

    // Drop on a grouped cell → change progress (and optionally group)
    if (overData.type === 'status-cell') {
      var colId = overData.columnId;
      if (colId === 'overdue') return;
      var targetCol = BOARD_COLUMNS.find(function (c) { return c.id === colId; });
      if (!targetCol || targetCol.progressValue == null) return;
      var currentColumn = getTaskBoardColumn(task);
      if (currentColumn !== colId) {
        onUpdateProgress(task.id, targetCol.progressValue);
      }
      // Also move to group if different
      if (onMoveTaskToGroup && overData.groupId !== undefined) {
        onMoveTaskToGroup(task.id, overData.groupId);
      }
      return;
    }

    // Drop on a flat column → change progress only
    var targetColumnId = String(over.id).replace('column-', '');
    if (targetColumnId === 'overdue') return;
    var targetColFlat = BOARD_COLUMNS.find(function (c) { return c.id === targetColumnId; });
    if (!targetColFlat || targetColFlat.progressValue == null) return;
    var currentCol = getTaskBoardColumn(task);
    if (currentCol === targetColumnId) return;
    onUpdateProgress(task.id, targetColFlat.progressValue);
  };

  var handleDragCancel = function () {
    setActiveId(null);
    setActiveTask(null);
  };

  var handleColumnAddTask = function (column) {
    var progress = column.progressValue != null ? column.progressValue : 50;
    onAddTask(progress);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}
    >
      {hasGroups ? (
        /* ── Grouped row-based layout ─────────────────────────── */
        <div style={{ padding: '12px' }}>
          {/* Column headers row */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', paddingLeft: '4px' }}>
            {BOARD_COLUMNS.map(function (col) {
              var colTasks = (tasks || []).filter(function (t) { return getTaskBoardColumn(t) === col.id; });
              return (
                <div key={col.id} style={{ flex: 1, minWidth: 0, padding: '4px 6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{
                    fontSize: '10px', fontWeight: 400, padding: '2px 8px',
                    background: col.bg, color: col.color,
                    border: '1px solid ' + col.border, borderRadius: '2px',
                    whiteSpace: 'nowrap', lineHeight: '14px'
                  }}>{col.label}</span>
                  <span style={{ fontSize: '11px', fontWeight: 300, color: '#aaa' }}>{colTasks.length}</span>
                </div>
              );
            })}
          </div>

          {/* One row per group section */}
          {groupedDisplaySections.map(function (section, si) {
            var group = section.group;
            var sectionTasks = section.tasks || [];
            var isCollapsed = group && collapsedGroupIds && collapsedGroupIds[group.id];

            return (
              <div key={group ? group.id : 'ungrouped-' + si} style={{ marginBottom: '8px' }}>
                {/* Group header — droppable */}
                {group && (
                  <GroupHeaderDropZone
                    group={group}
                    isCollapsed={isCollapsed}
                    onToggleCollapse={onToggleGroupCollapse}
                    taskCount={sectionTasks.length}
                  />
                )}
                {/* Ungrouped tasks have no header in the board view */}

                {/* Status columns row for this group */}
                {(!group || !isCollapsed) && (
                  <div style={{ display: 'flex', gap: '8px', paddingLeft: '4px' }}>
                    {BOARD_COLUMNS.map(function (col) {
                      var cellTasks = sectionTasks.filter(function (t) { return getTaskBoardColumn(t) === col.id; });
                      return (
                        <GroupedStatusCell
                          key={col.id}
                          column={col}
                          groupId={group ? group.id : null}
                          tasks={cellTasks}
                          getUserName={getUserName}
                          getUserColor={getUserColor}
                          activeId={activeId}
                          onUpdateTask={onUpdateTask}
                          canEdit={canEdit}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Flat column layout (no groups) ────────────────────── */
        <div style={{ display: 'flex', gap: '12px', padding: '12px', overflowX: 'auto', alignItems: 'flex-start' }}>
          {BOARD_COLUMNS.map(function (col) {
            return (
              <StatusColumn key={col.id} column={col} tasks={columnTasks[col.id] || []}
                getUserName={getUserName} getUserColor={getUserColor} activeId={activeId}
                onAddTask={handleColumnAddTask} onUpdateTask={onUpdateTask} canEdit={canEdit} />
            );
          })}
        </div>
      )}

      <DragOverlay dropAnimation={null}>
        {activeTask ? (
          <TaskCardOverlay task={activeTask} getUserName={getUserName} getUserColor={getUserColor} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// TASK GROUP COMPONENTS
// ══════════════════════════════════════════════════════════════════════════

var GROUP_COLORS = [
  '#689581', '#5B7A99', '#9F902A', '#934948',
  '#8B5E3C', '#8A5A7A', '#5A7A8A', '#D4824A'
];

// Sortable task row — long-press on the task number cell triggers drag (matches TimelinesPage behaviour).
// Uses useSortable for smooth slide animation and isDragging highlight.
// activeDragTaskId + taskGroupMap let us suppress the sort-shift transform for
// rows in a different group than the dragged task, preventing the visual
// "replacement" effect across groups while keeping same-group reorder smooth.
function SortableTaskRow({ taskId, disabled, children, style: rowStyle, taskGroupMap, activeDragTaskId }) {
  var { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: 'task-drag-' + taskId,
    disabled: disabled,
    data: { type: 'task-group-drag', taskId: taskId }
  });
  // Only apply sort-shift transform for items in the same group as the dragged task.
  // Cross-group items stay put — no visual replacement.
  var applyTransform = true;
  if (!isDragging && activeDragTaskId) {
    var myGroup = taskGroupMap ? (taskGroupMap[String(taskId)] || null) : null;
    var dragGroup = taskGroupMap ? (taskGroupMap[String(activeDragTaskId)] || null) : null;
    if (myGroup !== dragGroup) applyTransform = false;
  }
  var style = {
    ...rowStyle,
    transform: applyTransform ? CSS.Transform.toString(transform) : undefined,
    transition: transition,
    opacity: isDragging ? 0.5 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      {typeof children === 'function' ? children(listeners) : children}
    </tr>
  );
}

// Droppable wrapper for group header rows in the table — allows dropping tasks onto group headers
function TableGroupHeaderDrop({ groupId, children }) {
  var { setNodeRef, isOver } = useDroppable({
    id: 'table-group-row-drop-' + groupId,
    data: { type: 'group-drop', groupId: groupId }
  });
  return (
    <div ref={setNodeRef} style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      background: isOver ? BRIO_GOLD + '14' : 'transparent',
      borderRadius: '3px', transition: 'background 0.15s ease',
      margin: '-2px -4px', padding: '2px 4px'
    }}>
      {children}
    </div>
  );
}

// Droppable wrapper for the ungrouped section header row in the table
function TableUngroupedHeaderDrop({ taskCount }) {
  var { setNodeRef, isOver } = useDroppable({
    id: 'table-ungrouped-row-drop',
    data: { type: 'ungrouped-drop' }
  });
  return (
    <div ref={setNodeRef} style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '2px 8px 2px 4px', borderRadius: '3px',
      background: isOver ? '#99999920' : 'transparent',
      transition: 'background 0.15s ease'
    }}>
      <span style={{ fontSize: '11px', fontWeight: 400, color: '#aaa', fontFamily: 'Raleway, sans-serif' }}>
        Ungrouped
      </span>
      <span style={{ fontSize: '10px', fontWeight: 300, color: '#ccc', fontFamily: 'Raleway, sans-serif' }}>
        ({taskCount})
      </span>
    </div>
  );
}

// Droppable <tbody> wrapper — makes the entire task area of a group a drop zone
function GroupTbodyDrop({ groupId, groupColor, children }) {
  var { setNodeRef, isOver } = useDroppable({
    id: 'tbody-drop-' + (groupId != null ? groupId : 'ungrouped'),
    data: groupId != null ? { type: 'group-drop', groupId: groupId } : { type: 'ungrouped-drop' }
  });
  var bg = isOver ? ((groupColor || '#999') + '0c') : 'transparent';
  return (
    <tbody ref={setNodeRef} style={{ background: bg, transition: 'background 0.15s ease' }}>
      {children}
    </tbody>
  );
}

// A single group tab in the tab bar — draggable (reorder) + droppable (task assignment)
function GroupTab({ group, isActive, isEditing, onSelect, onStartEdit, onFinishEdit, onDelete }) {
  var [localName, setLocalName] = useState(group.name);
  var inputRef = useRef(null);

  var drop = useDroppable({
    id: 'sidebar-group-drop-' + group.id,
    data: { type: 'group-drop', groupId: group.id }
  });
  var drag = useDraggable({
    id: 'group-drag-' + group.id,
    data: { type: 'group-reorder', groupId: group.id }
  });

  var combinedRef = useCallback(function (node) {
    drop.setNodeRef(node);
    drag.setNodeRef(node);
  }, [drop.setNodeRef, drag.setNodeRef]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function () {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(function () { setLocalName(group.name); }, [group.name]);

  var color = group.color || BRIO_GOLD;
  var isOver = drop.isOver;

  function handleKeyDown(e) {
    if (e.key === 'Enter') { onFinishEdit(localName); }
    if (e.key === 'Escape') { setLocalName(group.name); onFinishEdit(group.name); }
  }

  return (
    <div
      ref={combinedRef}
      {...drag.attributes}
      {...drag.listeners}
      onClick={!isEditing ? onSelect : undefined}
      onDoubleClick={function (e) { e.stopPropagation(); onStartEdit(); }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '5px',
        padding: '4px 8px 4px 10px',
        fontSize: '11px', fontFamily: 'Raleway, sans-serif',
        fontWeight: isActive ? 500 : 300,
        borderBottom: isActive ? '2px solid ' + color : isOver ? '2px solid ' + color + '60' : '2px solid transparent',
        background: isActive ? color + '0E' : isOver ? color + '08' : 'transparent',
        color: isActive ? color : isOver ? color : '#666',
        cursor: drag.isDragging ? 'grabbing' : 'pointer',
        transition: 'all 0.15s ease',
        opacity: drag.isDragging ? 0.45 : 1,
        transform: drag.transform
          ? 'translate3d(' + drag.transform.x + 'px,' + drag.transform.y + 'px,0)'
          : undefined,
        whiteSpace: 'nowrap', flexShrink: 0, userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'none', position: 'relative'
      }}
    >
      {/* Color dot */}
      <span style={{
        width: '6px', height: '6px', borderRadius: '50%',
        background: color, flexShrink: 0
      }} />

      {/* Name */}
      {isEditing ? (
        <input
          ref={inputRef}
          value={localName}
          onChange={function (e) { setLocalName(e.target.value); }}
          onBlur={function () { onFinishEdit(localName); }}
          onKeyDown={handleKeyDown}
          onClick={function (e) { e.stopPropagation(); }}
          style={{
            fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400,
            border: 'none', outline: 'none', background: 'transparent',
            padding: 0, minWidth: '60px',
            width: Math.max(60, localName.length * 7) + 'px',
            color: color
          }}
        />
      ) : (
        <span>{group.name}</span>
      )}

      {/* Delete */}
      {!isEditing && (
        <button
          onClick={function (e) { e.stopPropagation(); onDelete(); }}
          style={{
            display: 'inline-flex', alignItems: 'center', border: 'none',
            background: 'transparent', padding: '0 0 0 1px',
            cursor: 'pointer', color: '#ccc', lineHeight: 1
          }}
          title="Delete group"
        >
          <X size={9} />
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ══════════════════════════════════════════════════════════════════════════

export default function ProjectTasksPage({
  currentUser,
  projects,
  users,
  priorityOptions: priorityOptionsProp,
  taskCategories: taskCategoriesProp
}) {
  var PRIORITY_OPTIONS = useMemo(function () {
    return buildPriorityOptions(priorityOptionsProp);
  }, [priorityOptionsProp]);

  // Dynamic task categories from settings (admin-managed). Falls back to built-in defaults.
  var TASK_CATEGORIES = useMemo(function () {
    if (taskCategoriesProp && taskCategoriesProp.length > 0) {
      return taskCategoriesProp.map(function (c) { return c.name; });
    }
    return ['Follow up','BRIO','Client','Structure','MEP','Landscape','Consultants','Vendors','Site coordination','PMC','MOM_Linked'];
  }, [taskCategoriesProp]);

  var CATEGORY_COLORS = useMemo(function () {
    var source = (taskCategoriesProp && taskCategoriesProp.length > 0)
      ? taskCategoriesProp
      : [
          { name: 'Follow up',         color: '#7B6B3A' },
          { name: 'BRIO',              color: '#9f902a' },
          { name: 'Client',            color: '#4A7A9A' },
          { name: 'Structure',         color: '#5A7A8A' },
          { name: 'MEP',               color: '#8B5E3C' },
          { name: 'Landscape',         color: '#5C8A5A' },
          { name: 'Consultants',       color: '#7A6890' },
          { name: 'Vendors',           color: '#8A5A7A' },
          { name: 'Site coordination', color: '#6A6A8A' },
          { name: 'PMC',               color: '#934948' },
          { name: 'MOM_Linked',        color: '#6B5EA8' }
        ];
    var map = {};
    source.forEach(function (c) {
      map[c.name] = { color: c.color, bg: c.color + '15', border: c.color + '30' };
    });
    return map;
  }, [taskCategoriesProp]);
  // ── Role checks ────────────────────────────────────────────────────
  var isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';
  var isSupervisor = currentUser?.role === 'supervisor';
  // canEdit is resolved after allocatedUserIds is computed (see below)

  // ── Project selector state ─────────────────────────────────────────
  var sortedProjects = useMemo(function () {
    if (!projects) return [];
    return [...projects].sort(function (a, b) { return (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''); });
  }, [projects]);

  var STORAGE_KEY_PROJECT = 'brio_projectTasks_lastProjectId';

  var [selectedProjectId, setSelectedProjectId] = useState(function () {
    try {
      var saved = localStorage.getItem(STORAGE_KEY_PROJECT);
      return saved ? Number(saved) : null;
    } catch (_) { return null; }
  });

  // Persist selection to localStorage
  useEffect(function () {
    if (selectedProjectId) {
      try { localStorage.setItem(STORAGE_KEY_PROJECT, String(selectedProjectId)); } catch (_) { /* noop */ }
    }
  }, [selectedProjectId, STORAGE_KEY_PROJECT]);

  useEffect(function () {
    if (sortedProjects.length > 0) {
      if (selectedProjectId) {
        // Verify saved project still exists
        var exists = sortedProjects.some(function (p) { return String(p.id) === String(selectedProjectId); });
        if (!exists) setSelectedProjectId(sortedProjects[0].id);
      } else {
        setSelectedProjectId(sortedProjects[0].id);
      }
    }
  }, [sortedProjects, selectedProjectId]);

  var selectedProject = useMemo(function () {
    if (!selectedProjectId || !sortedProjects.length) return null;
    return sortedProjects.find(function (p) { return String(p.id) === String(selectedProjectId); }) || null;
  }, [selectedProjectId, sortedProjects]);

  // ── Week navigation state ──────────────────────────────────────────
  var [currentWeekId, setCurrentWeekId] = useState(getCurrentWeekId);
  var [centerWeekId, setCenterWeekId] = useState(getCurrentWeekId);

  var weekTabs = useMemo(function () { return getWeekTabs(centerWeekId); }, [centerWeekId]);

  var handlePrevWeeks = function () { setCenterWeekId(function (c) { return offsetWeekId(c, -5); }); };
  var handleNextWeeks = function () { setCenterWeekId(function (c) { return offsetWeekId(c, 5); }); };
  var handleSelectWeek = function (wid) { setCurrentWeekId(wid); };
  var handleTodayClick = function () {
    var today = getCurrentWeekId();
    setCurrentWeekId(today);
    setCenterWeekId(today);
  };

  // ── Data hooks ─────────────────────────────────────────────────────
  var tasksHook = useProjectWeeklyTasks(selectedProjectId, currentWeekId);
  var allocationsHook = useHFBProjectAllocations(currentWeekId);

  // ── Allocated team members for dropdown ────────────────────────────
  var allocatedUserIds = useMemo(function () {
    if (!selectedProjectId || !allocationsHook.allocations) return [];
    var ids = [];
    allocationsHook.allocations.forEach(function (a) {
      if (String(a.projectId) === String(selectedProjectId)) {
        if (ids.indexOf(a.userId) === -1) ids.push(a.userId);
      }
    });
    return ids;
  }, [selectedProjectId, allocationsHook.allocations]);

  // Users assigned to the project can edit tasks
  var isAssignedToProject = useMemo(function () {
    if (!currentUser?.id || !allocatedUserIds.length) return false;
    return allocatedUserIds.some(function (id) { return String(id) === String(currentUser.id); });
  }, [currentUser?.id, allocatedUserIds]);

  var canEdit = isAdmin || isSupervisor || isAssignedToProject;
  var canDelete = canEdit;

  var allocatedUsers = useMemo(function () {
    if (!users || !allocatedUserIds.length) return [];
    return users.filter(function (u) {
      return allocatedUserIds.some(function (id) { return String(id) === String(u.id); })
        && u.status !== 'inactive' && u.isActive !== false;
    });
  }, [users, allocatedUserIds]);

  var getUserName = useCallback(function (userId) {
    if (!userId || !users) return '—';
    var found = users.find(function (u) {
      return String(u.id) === String(userId);
    });
    if (!found) return '—';
    var name = found.name || '';
    // Capitalize first letter of each word
    var parts = name.split(/\s+/).map(function (w) {
      return w.charAt(0).toUpperCase() + w.slice(1);
    });
    var isSupervisor = found.role === 'admin' || found.role === 'admin-pa' || found.role === 'supervisor';
    if (isSupervisor || parts.length <= 1) {
      return parts.join(' ');
    }
    // Non-supervisor: first name + last initial
    return parts[0] + ' ' + parts[parts.length - 1].charAt(0) + '.';
  }, [users]);

  // Returns BRIO_GOLD at full intensity for supervisors/admins,
  // 10% desaturated for regular users
  var BRIO_GOLD_MUTED = '#A49745';
  var getUserColor = useCallback(function (userId) {
    if (!userId || !users) return BRIO_GOLD_MUTED;
    var user = users.find(function (u) { return String(u.id) === String(userId); });
    if (!user) return BRIO_GOLD_MUTED;
    if (user.role === 'admin' || user.role === 'admin-pa' || user.role === 'supervisor') {
      return BRIO_GOLD;
    }
    return BRIO_GOLD_MUTED;
  }, [users]);

  // ── Fullscreen state (CSS fixed-position, same as Team Allocation page) ──
  var [isFullscreen, setIsFullscreen] = useState(false);

  var toggleFullscreen = useCallback(function () {
    setIsFullscreen(function (v) { return !v; });
  }, []);

  // ── Sort / Filter / ViewMode — persisted in Supabase, live-synced across users ──
  var viewStateHook = useProjectTaskViewState(selectedProjectId, currentUser?.id);
  var sortRules = viewStateHook.sortRules;
  var setSortRules = viewStateHook.setSortRules;
  var filterRules = viewStateHook.filterRules;
  var setFilterRules = viewStateHook.setFilterRules;
  var viewMode = viewStateHook.viewMode;
  var setViewMode = viewStateHook.setViewMode;
  var saveForEveryone = viewStateHook.saveForEveryone;
  var saveForMe = viewStateHook.saveForMe;

  // ── Sort & Filter local UI state ───────────────────────────────────
  var [isManualOrder, setIsManualOrder] = useState(false); // true after DnD reorder — skips applySorts
  var [showSortPanel, setShowSortPanel] = useState(false);
  var [showFilterPanel, setShowFilterPanel] = useState(false);

  // When sort rules arrive from another user's realtime update, clear
  // any local manual-drag order so the new rules take effect immediately.
  useEffect(function () { setIsManualOrder(false); }, [sortRules]);

  var closeSortPanel = useCallback(function () { setShowSortPanel(false); }, []);
  var closeFilterPanel = useCallback(function () { setShowFilterPanel(false); }, []);

  var [showSettingsPanel, setShowSettingsPanel] = useState(false);
  var settingsPanelRef = useRef(null);
  var importCsvRef = useRef(null);
  var importXlsxRef = useRef(null);

  useEffect(function () {
    if (!showSettingsPanel) return;
    function handleOutside(e) {
      if (settingsPanelRef.current && !settingsPanelRef.current.contains(e.target)) {
        setShowSettingsPanel(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return function () { document.removeEventListener('mousedown', handleOutside); };
  }, [showSettingsPanel]);

  // ── Group state ────────────────────────────────────────────────────
  // Groups + task-group map are shared via Supabase (all users see the same groups)
  var groupsHook = useProjectTaskGroups(selectedProjectId);
  var groups = groupsHook.groups;
  var setGroups = groupsHook.setGroups;
  var taskGroupMap = groupsHook.taskGroupMap;
  var setTaskGroupMap = groupsHook.setTaskGroupMap;

  var [activeGroupTab, setActiveGroupTab] = useState(function () {
    if (!selectedProjectId) return 'all';
    try { return localStorage.getItem('brio_taskGroupActiveTab_' + selectedProjectId) || 'all'; } catch (_) { return 'all'; }
  });
  var [isGroupPanelOpen, setIsGroupPanelOpen] = useState(function () {
    if (!selectedProjectId) return false;
    try { return localStorage.getItem('brio_taskGroupPanelOpen_' + selectedProjectId) === 'true'; } catch (_) { return false; }
  });
  var [collapsedGroupIds, setCollapsedGroupIds] = useState(function () { // { [groupId]: true } = collapsed
    if (!selectedProjectId) return {};
    try { var s = localStorage.getItem('brio_taskGroupCollapsed_' + selectedProjectId); return s ? JSON.parse(s) : {}; } catch (_) { return {}; }
  });
  var [editingGroupId, setEditingGroupId] = useState(null);
  var [groupDragActiveId, setGroupDragActiveId] = useState(null);
  var [pageZoomIndex, setPageZoomIndex] = useState(PAGE_ZOOM_DEFAULT);

  // Restore UI-only state from localStorage when project changes (groups/map reload via hook)
  useEffect(function () {
    if (!selectedProjectId) {
      setActiveGroupTab('all');
      setIsGroupPanelOpen(false);
      setCollapsedGroupIds({});
      setEditingGroupId(null);
      return;
    }
    try { setActiveGroupTab(localStorage.getItem('brio_taskGroupActiveTab_' + selectedProjectId) || 'all'); } catch (_) { setActiveGroupTab('all'); }
    try { setIsGroupPanelOpen(localStorage.getItem('brio_taskGroupPanelOpen_' + selectedProjectId) === 'true'); } catch (_) { setIsGroupPanelOpen(false); }
    try { var s = localStorage.getItem('brio_taskGroupCollapsed_' + selectedProjectId); setCollapsedGroupIds(s ? JSON.parse(s) : {}); } catch (_) { setCollapsedGroupIds({}); }
    setEditingGroupId(null);
  }, [selectedProjectId]);

  // Persist UI-only state to localStorage (collapsed, panel open, active tab — not shared)
  useEffect(function () {
    if (!selectedProjectId) return;
    try { localStorage.setItem('brio_taskGroupCollapsed_' + selectedProjectId, JSON.stringify(collapsedGroupIds)); } catch (_) {}
  }, [collapsedGroupIds, selectedProjectId]);

  useEffect(function () {
    if (!selectedProjectId) return;
    try { localStorage.setItem('brio_taskGroupPanelOpen_' + selectedProjectId, String(isGroupPanelOpen)); } catch (_) {}
  }, [isGroupPanelOpen, selectedProjectId]);

  useEffect(function () {
    if (!selectedProjectId) return;
    try { localStorage.setItem('brio_taskGroupActiveTab_' + selectedProjectId, activeGroupTab); } catch (_) {}
  }, [activeGroupTab, selectedProjectId]);

  // If the activeGroupTab refers to a group that no longer exists (deleted from
  // another device or tab), reset to 'all' so tasks are never hidden behind a
  // ghost filter. Guard against running before groups have loaded by checking
  // that loading has finished; groups can legitimately be [] on a fresh project.
  useEffect(function () {
    if (groupsHook.loading) return;
    if (activeGroupTab === 'all' || activeGroupTab === 'ungrouped') return;
    var exists = groups.some(function (g) { return g.id === activeGroupTab; });
    if (!exists) setActiveGroupTab('all');
  }, [groups, activeGroupTab, groupsHook.loading]);

  // Group DnD sensors — long-press on serial number to trigger drag (300ms delay + 5px tolerance)
  // PointerSensor handles mouse, stylus, and touch via Pointer Events API
  // TouchSensor provides fallback for older Android browsers using Touch Events API
  var groupDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 300, tolerance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } })
  );

  // ── Export helpers ──────────────────────────────────────────────────

  var exportCSV = function () {
    var headers = ['Task', 'Categories', 'Assigned To', 'Priority', 'Start Date', 'Due Date', 'Progress', 'Daily Update'];
    var escape = function (v) { return '"' + String(v || '').replace(/"/g, '""') + '"'; };
    var rows = displayTasks.map(function (t) {
      return [
        escape(t.task),
        escape((t.categories || []).join('; ')),
        escape((t.assignedUserIds || []).map(function (id) { return getUserName(id); }).join('; ')),
        escape(t.priority),
        t.startDate || '',
        t.completionDate || '',
        t.progress + '%',
        escape(t.updates5pm)
      ].join(',');
    });
    var csv = [headers.join(',')].concat(rows).join('\r\n');
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = (selectedProject ? selectedProject.name : 'tasks') + '_' + currentWeekId + '.csv';
    a.click();
    URL.revokeObjectURL(url);
    setShowSettingsPanel(false);
  };

  var exportExcel = async function () {
    try {
      var ExcelJS = (await import('exceljs')).default;
      var wb = new ExcelJS.Workbook();
      var ws = wb.addWorksheet('Tasks');
      ws.columns = [
        { header: 'Task', key: 'task', width: 40 },
        { header: 'Categories', key: 'categories', width: 22 },
        { header: 'Assigned To', key: 'assigned', width: 25 },
        { header: 'Priority', key: 'priority', width: 15 },
        { header: 'Start Date', key: 'startDate', width: 12 },
        { header: 'Due Date', key: 'dueDate', width: 12 },
        { header: 'Progress', key: 'progress', width: 10 },
        { header: 'Daily Update', key: 'update', width: 40 }
      ];
      displayTasks.forEach(function (t) {
        ws.addRow({
          task: t.task || '',
          categories: (t.categories || []).join('; '),
          assigned: (t.assignedUserIds || []).map(function (id) { return getUserName(id); }).join('; '),
          priority: t.priority || '',
          startDate: t.startDate || '',
          dueDate: t.completionDate || '',
          progress: t.progress + '%',
          update: t.updates5pm || ''
        });
      });
      var _getExcelStyle = (await import('../../utils/excelStyles')).getExcelStyle;
      ws.getRow(1).font = { ..._getExcelStyle('style1'), bold: true };
      ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAF8' } };
      var buffer = await wb.xlsx.writeBuffer();
      var blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = (selectedProject ? selectedProject.name : 'tasks') + '_' + currentWeekId + '.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
      alert('Excel export failed: ' + err.message);
    }
    setShowSettingsPanel(false);
  };

  var exportPDF = function () {
    var projectName = selectedProject ? selectedProject.name : 'Tasks';

    // HTML-escape helper
    function esc(str) {
      return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    // Inline tag builders
    function catTag(cat) {
      var cc = CATEGORY_COLORS[cat] || { color: '#666666', bg: 'rgba(102,102,102,0.08)', border: 'rgba(102,102,102,0.2)' };
      return '<span style="display:inline-block;font-size:9px;font-weight:500;padding:1px 6px;' +
        'background:' + cc.bg + ';color:' + cc.color + ';border:1px solid ' + cc.border + ';' +
        'border-radius:2px;margin:1px 2px 1px 0;white-space:nowrap;line-height:15px">' + esc(cat) + '</span>';
    }

    function assignedTag(uid) {
      var name = getUserName(uid);
      var uc = getUserColor(uid);
      return '<span style="display:inline-block;font-size:9px;font-weight:400;padding:1px 6px;' +
        'background:' + uc + '22;color:' + uc + ';border:1px solid ' + uc + '40;' +
        'border-radius:2px;margin:1px 2px 1px 0;white-space:nowrap;line-height:15px">' + esc(name) + '</span>';
    }

    function priorityTag(p) {
      var opt = PRIORITY_OPTIONS.find(function (o) { return o.value === p; });
      if (!opt) return esc(p || '');
      return '<span style="display:inline-block;font-size:9px;font-weight:500;padding:1px 6px;' +
        'background:' + opt.bg + ';color:' + opt.color + ';border:1px solid ' + opt.color + '40;' +
        'border-radius:2px;white-space:nowrap;line-height:15px">' + esc(p) + '</span>';
    }

    function progressBadge(progress, completionDate) {
      var overdue = isOverdueCheck(completionDate, progress);
      var opt;
      if (overdue) {
        opt = { label: 'Overdue', color: BRIO_DEEP_RED, bg: '#6B202012', border: BRIO_DEEP_RED + '30' };
      } else {
        opt = STATUS_OPTIONS.slice().reverse().find(function (o) { return progress >= o.value; }) || STATUS_OPTIONS[0];
      }
      var label = overdue ? 'Overdue' : opt.label;
      return '<span style="display:inline-block;font-size:9px;font-weight:500;padding:1px 8px;' +
        'background:' + opt.bg + ';color:' + opt.color + ';border:1px solid ' + opt.border + ';' +
        'border-radius:2px;white-space:nowrap;line-height:15px;box-sizing:border-box;">' + esc(label) + ' (' + progress + '%)</span>';
    }

    var catW = columnMinWidths.category;
    var assignW = columnMinWidths.assigned;
    var prioW = columnMinWidths.priority;
    var statusW = columnMinWidths.status;

    var rows = displayTasks.map(function (t) {
      var taskOverdue = isOverdueCheck(t.completionDate, t.progress);
      var rowBg = taskOverdue ? 'rgba(107,32,32,0.03)' : (t.progress === 100 ? 'rgba(104,149,129,0.03)' : '#ffffff');
      var taskNameStyle = t.progress === 100 ? 'text-decoration:line-through;color:#999' : '';
      var catItems = (t.categories || []);
      var assignedItems = (t.assignedUserIds || []);
      // Wrap multiple tags in a flex-wrap container so they stay inside the column
      function multiLineOrSingle(items, tagFn) {
        if (items.length === 0) return '<span style="color:#ccc">—</span>';
        if (items.length === 1) return tagFn(items[0]);
        return '<div style="display:flex;flex-wrap:wrap;gap:3px">' + items.map(tagFn).join('') + '</div>';
      }
      var cats = multiLineOrSingle(catItems, catTag);
      var assigned = multiLineOrSingle(assignedItems, assignedTag);
      var dueCell = t.completionDate
        ? (taskOverdue
          ? '<span style="color:' + BRIO_DEEP_RED + ';font-weight:500">' + esc(t.completionDate) + '</span>'
          : esc(t.completionDate))
        : '<span style="color:#ccc">—</span>';
      var tdBase = 'padding:5px 8px;vertical-align:top;border-right:1px solid ' + BRIO_BORDER + ';font-size:10px;line-height:1.4;overflow:hidden;';
      var tdC = tdBase + 'word-break:break-word;';
      var tdCA = tdBase + 'text-align:center;word-break:break-word;';
      var tdDate = tdBase + 'text-align:center;white-space:nowrap;';
      return '<tr style="background:' + rowBg + ';border-top:1px solid ' + BRIO_BORDER + '">' +
        '<td style="' + tdBase + '"><span style="' + taskNameStyle + '">' + esc(t.task || '') + '</span>' +
          (t.isCarriedForward ? '<br><span style="font-size:8px;color:' + BRIO_ORANGE + '">\u21ba ' + esc(t.originWeekId || '') + '</span>' : '') +
        '</td>' +
        '<td style="' + tdC + '">' + cats + '</td>' +
        '<td style="' + tdC + '">' + assigned + '</td>' +
        '<td style="' + tdCA + '">' + (t.priority ? priorityTag(t.priority) : '<span style="color:#ccc">—</span>') + '</td>' +
        '<td style="' + tdDate + '">' + (t.startDate ? esc(t.startDate) : '<span style="color:#ccc">—</span>') + '</td>' +
        '<td style="' + tdDate + '">' + dueCell + '</td>' +
        '<td style="' + tdC + '">' + esc(t.updates5pm || '') + '</td>' +
        '<td style="' + tdC + 'border-right:none;">' + progressBadge(t.progress || 0, t.completionDate) + '</td>' +
        '</tr>';
    }).join('');

    var html = '<!DOCTYPE html><html><head>' +
      '<meta charset="utf-8">' +
      '<title>' + esc(projectName) + ' \u2013 ' + esc(currentWeekId) + '</title>' +
      '<link rel="preconnect" href="https://fonts.googleapis.com">' +
      '<link href="https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600&display=swap" rel="stylesheet">' +
      '<style>' +
      '* { box-sizing: border-box; }' +
      'body { font-family: Raleway, Arial, sans-serif; font-size: 10px; margin: 24px; color: #333; background: #fff; }' +
      'h2 { font-size: 14px; font-weight: 600; margin: 0 0 3px; color: #333; letter-spacing: 0.01em; }' +
      '.subtitle { font-size: 9px; color: #999; margin: 0 0 16px; font-weight: 400; }' +
      'table { width: 100%; border-collapse: collapse; table-layout: fixed; border: 1px solid ' + BRIO_BORDER + '; }' +
      'th { text-align: left; padding: 10px 12px; font-size: 10px; font-weight: 500; color: ' + BRIO_GREY + '; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap; background: ' + BRIO_BG + '; border-bottom: 2px solid ' + BRIO_BORDER + '; border-right: 1px solid ' + BRIO_BORDER + '; overflow: hidden; }' +
      'th:last-child { border-right: none; }' +
      'td { overflow: hidden; }' +
      '@media print { body { margin: 10mm; } @page { margin: 10mm; size: A3 landscape; } }' +
      '</style>' +
      '</head><body>' +
      '<h2>' + esc(projectName) + '</h2>' +
      '<p class="subtitle">Week ' + esc(currentWeekId) + ' &nbsp;&middot;&nbsp; ' + displayTasks.length + ' task' + (displayTasks.length !== 1 ? 's' : '') + '</p>' +
      '<table>' +
      '<colgroup>' +
      '<col>' +
      '<col style="width:' + catW + 'px">' +
      '<col style="width:' + assignW + 'px">' +
      '<col style="width:' + prioW + 'px">' +
      '<col style="width:82px">' +
      '<col style="width:82px">' +
      '<col style="width:250px">' +
      '<col style="width:' + statusW + 'px">' +
      '</colgroup>' +
      '<thead><tr>' +
      '<th>Task</th>' +
      '<th>Category</th>' +
      '<th>Assigned To</th>' +
      '<th style="text-align:center">Priority</th>' +
      '<th style="text-align:center">Start</th>' +
      '<th style="text-align:center">Due</th>' +
      '<th>Daily Update</th>' +
      '<th>Progress</th>' +
      '</tr></thead>' +
      '<tbody>' + rows + '</tbody>' +
      '</table>' +
      '</body></html>';

    var win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); win.focus(); setTimeout(function () { win.print(); }, 600); }
    setShowSettingsPanel(false);
  };

  // ── Import helpers ──────────────────────────────────────────────────

  var parseCSVLine = function (line) {
    var result = []; var cur = ''; var inQ = false;
    for (var i = 0; i < line.length; i++) {
      var ch = line[i];
      if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else { inQ = !inQ; } }
      else if (ch === ',' && !inQ) { result.push(cur); cur = ''; }
      else { cur += ch; }
    }
    result.push(cur);
    return result;
  };

  // Full RFC-4180 CSV parser — handles quoted fields with embedded newlines.
  // Returns an array of records (each record is an array of field strings).
  var parseCSVFull = function (text) {
    var records = []; var record = []; var cur = ''; var inQ = false;
    var i = 0;
    while (i < text.length) {
      var ch = text[i];
      if (ch === '"') {
        if (inQ && text[i + 1] === '"') { cur += '"'; i += 2; continue; }
        inQ = !inQ; i++; continue;
      }
      if (ch === ',' && !inQ) { record.push(cur); cur = ''; i++; continue; }
      if ((ch === '\r' || ch === '\n') && !inQ) {
        if (ch === '\r' && text[i + 1] === '\n') i++;
        record.push(cur); cur = '';
        records.push(record); record = [];
        i++; continue;
      }
      cur += ch; i++;
    }
    record.push(cur);
    if (record.some(function (f) { return f.trim(); })) records.push(record);
    return records;
  };

  // ── Shared import helpers ───────────────────────────────────────────

  // Build a comprehensive name→id map for import matching.
  // Indexes every user by: raw DB name, formatted display name,
  // full capitalized name, and (when unambiguous) first name only.
  var buildNameToIdMap = useCallback(function () {
    var map = {};
    var firstNameCount = {};
    (users || []).forEach(function (u) {
      if (!u.name) return;
      var raw = u.name.trim();

      // Raw DB name (lowercase)
      map[raw.toLowerCase()] = u.id;

      // Formatted display name (e.g. "John S." for regular users)
      var display = getUserName(u.id);
      if (display && display !== '—') map[display.trim().toLowerCase()] = u.id;

      // Full capitalized name (all words) — covers "John Smith" even for
      // regular users whose display name is "John S."
      var full = raw.split(/\s+/).map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
      }).join(' ');
      map[full.toLowerCase()] = u.id;

      // Track first-name frequency for unambiguous first-name matching
      var first = raw.split(/\s+/)[0].toLowerCase();
      firstNameCount[first] = (firstNameCount[first] || 0) + 1;
    });

    // Add first-name-only entries only when that first name is unique
    (users || []).forEach(function (u) {
      if (!u.name) return;
      var first = u.name.trim().split(/\s+/)[0].toLowerCase();
      if (firstNameCount[first] === 1) map[first] = u.id;
    });

    return map;
  }, [users, getUserName]);

  // Split an "Assigned To" cell value by both ';' and ',' separators,
  // resolve each token against the name map, and return unique matched IDs.
  var resolveAssignedNames = useCallback(function (cellValue, nameToId) {
    var tokens = cellValue.split(/[;,]/).map(function (n) { return n.trim(); }).filter(Boolean);
    var seen = {};
    var ids = [];
    tokens.forEach(function (token) {
      var key = token.toLowerCase();
      var id = nameToId[key];
      // Fallback: substring match against full user names
      if (!id) {
        var matched = (users || []).find(function (u) {
          if (!u.name) return false;
          var full = u.name.trim().toLowerCase();
          return full.indexOf(key) !== -1 || key.indexOf(full) !== -1;
        });
        if (matched) id = matched.id;
      }
      if (id && !seen[id]) { seen[id] = true; ids.push(id); }
    });
    return ids;
  }, [users]);

  var handleImportCSV = function (e) {
    var file = e.target.files[0];
    if (!file) return;
    var reader = new FileReader();
    reader.onload = async function (ev) {
      // Use full RFC-4180 parser so quoted multi-line cells (e.g. bullet lists
      // in the Daily Update column) are captured correctly.
      var records = parseCSVFull(ev.target.result);
      if (records.length < 1) return;

      // Parse header row to locate columns by name
      var headers = records[0].map(function (h) { return h.trim().toLowerCase(); });
      var taskCol = headers.indexOf('task');
      var assignedCol = headers.indexOf('assigned to');
      var categoryCol = headers.findIndex(function (h) { return h === 'category' || h === 'categories'; });
      var priorityCol = headers.indexOf('priority');
      var dailyUpdateCol = headers.findIndex(function (h) { return h.indexOf('daily update') !== -1; });
      if (taskCol === -1) taskCol = 0; // fallback: first column

      var nameToId = buildNameToIdMap();

      var categoryLower = {};
      Object.keys(CATEGORY_COLORS).forEach(function (c) { categoryLower[c.toLowerCase()] = c; });

      var priorityLower = {};
      PRIORITY_OPTIONS.forEach(function (p) { priorityLower[p.value.toLowerCase()] = p.value; });

      for (var i = 1; i < records.length; i++) {
        var cols = records[i];
        var taskName = (cols[taskCol] || '').trim();
        if (!taskName) continue;

        var taskData = { task: taskName };

        // Assigned To → resolve names (multi) to user id array
        if (assignedCol !== -1 && cols[assignedCol]) {
          var ids = resolveAssignedNames(cols[assignedCol], nameToId);
          if (ids.length > 0) taskData.assignedUserIds = ids;
        }

        // Categories (semicolon or comma separated) — fuzzy-matched to nearest canonical name
        if (categoryCol !== -1 && cols[categoryCol]) {
          var cats = cols[categoryCol].split(/[;,]/).map(function (n) { return n.trim(); })
            .map(function (n) { return findClosestCategory(n, categoryLower); })
            .filter(Boolean);
          if (cats.length > 0) taskData.categories = cats;
        }

        // Priority
        if (priorityCol !== -1 && cols[priorityCol]) {
          var pVal = priorityLower[(cols[priorityCol] || '').trim().toLowerCase()];
          if (pVal) taskData.priority = pVal;
        }

        // Daily Update — preserve raw text including bullet formatting
        if (dailyUpdateCol !== -1 && cols[dailyUpdateCol]) {
          var updateText = cols[dailyUpdateCol].trim();
          if (updateText) taskData.updates5pm = updateText;
        }

        await tasksHook.addTask(taskData);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
    setShowSettingsPanel(false);
  };

  var handleImportExcel = function (e) {
    var file = e.target.files[0];
    if (!file) return;
    var reader = new FileReader();
    reader.onload = async function (ev) {
      try {
        var ExcelJS = (await import('exceljs')).default;
        var wb = new ExcelJS.Workbook();
        await wb.xlsx.load(ev.target.result);
        var ws = wb.worksheets[0];

        var nameToId = buildNameToIdMap();

        var categoryLower = {};
        Object.keys(CATEGORY_COLORS).forEach(function (c) { categoryLower[c.toLowerCase()] = c; });

        var priorityLower = {};
        PRIORITY_OPTIONS.forEach(function (p) { priorityLower[p.value.toLowerCase()] = p.value; });

        // ── Locate columns by reading the header row ───────────────────
        var headerRow = ws.getRow(1);
        var colIndex = {}; // key → 1-based column number
        headerRow.eachCell(function (cell, colNum) {
          var h = String(cell.value || '').trim().toLowerCase();
          colIndex[h] = colNum;
        });

        var taskCol     = colIndex['task']        || 1;
        var assignedCol = colIndex['assigned to'];
        var catCol      = colIndex['categories'] || colIndex['category'];
        var prioCol     = colIndex['priority'];
        var dailyUpdateCol = Object.keys(colIndex).find(function (k) { return k.indexOf('daily update') !== -1; });
        var duCol = dailyUpdateCol ? colIndex[dailyUpdateCol] : null;

        // ── Parse data rows ────────────────────────────────────────────
        var promises = [];
        ws.eachRow(function (row, rowNum) {
          if (rowNum === 1) return; // skip header
          var taskName = String(row.getCell(taskCol).value || '').trim();
          if (!taskName) return;

          var taskData = { task: taskName };

          // Assigned To → resolve names (multi) to user id array
          if (assignedCol) {
            var rawAssigned = String(row.getCell(assignedCol).value || '').trim();
            if (rawAssigned) {
              var ids = resolveAssignedNames(rawAssigned, nameToId);
              if (ids.length > 0) taskData.assignedUserIds = ids;
            }
          }

          // Categories → fuzzy-matched to nearest canonical name (semi or comma separated)
          if (catCol) {
            var rawCats = String(row.getCell(catCol).value || '').trim();
            if (rawCats) {
              var cats = rawCats.split(/[;,]/)
                .map(function (n) { return findClosestCategory(n.trim(), categoryLower); })
                .filter(Boolean);
              if (cats.length > 0) taskData.categories = cats;
            }
          }

          // Priority → matched priority value
          if (prioCol) {
            var rawPrio = String(row.getCell(prioCol).value || '').trim().toLowerCase();
            var matched = priorityLower[rawPrio];
            if (matched) taskData.priority = matched;
          }

          // Daily Update — preserve raw text including bullet formatting
          if (duCol) {
            var rawUpdate = String(row.getCell(duCol).value || '').trim();
            if (rawUpdate) taskData.updates5pm = rawUpdate;
          }

          promises.push(tasksHook.addTask(taskData));
        });
        await Promise.all(promises);
      } catch (err) {
        console.error('Excel import failed:', err);
        alert('Excel import failed: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
    setShowSettingsPanel(false);
  };

  var removeFilterChip = useCallback(function (idx) {
    setFilterRules(function (prev) { return prev.filter(function (_, i) { return i !== idx; }); });
  }, [setFilterRules]);

  var displayTasks = useMemo(function () {
    var filtered = applyFilters(tasksHook.tasks, filterRules, getUserName);
    // Skip applySorts when the user has manually reordered via drag-and-drop
    var sorted = isManualOrder ? filtered : applySorts(filtered, sortRules, getUserName, PRIORITY_OPTIONS, TASK_CATEGORIES, allocatedUsers);
    // Group tab filtering — only when the tab bar is open and a specific tab is selected
    if (isGroupPanelOpen && activeGroupTab !== 'all') {
      sorted = sorted.filter(function (t) {
        var tid = String(t.id);
        if (activeGroupTab === 'ungrouped') return !taskGroupMap[tid];
        return taskGroupMap[tid] === activeGroupTab;
      });
    }
    // Always push done tasks to the bottom
    var notDone = sorted.filter(function (t) { return t.progress < 100; });
    var done = sorted.filter(function (t) { return t.progress >= 100; });
    return notDone.concat(done);
  }, [tasksHook.tasks, filterRules, sortRules, isManualOrder, getUserName, PRIORITY_OPTIONS, TASK_CATEGORIES, isGroupPanelOpen, activeGroupTab, taskGroupMap]);

  // Grouped display: sections = [{ group: null|{...}, tasks: [...] }, ...]
  // Always group when groups exist — nested view is always the default when groups are defined.
  // When a specific group tab is active, fall through to flat single-section view for that group.
  var groupedDisplaySections = useMemo(function () {
    if (groups.length === 0 || (isGroupPanelOpen && activeGroupTab !== 'all')) {
      return [{ group: null, tasks: displayTasks }];
    }
    // Build ungrouped + each group's tasks
    var ungrouped = [];
    var byGroup = {};
    groups.forEach(function (g) { byGroup[g.id] = []; });
    displayTasks.forEach(function (t) {
      var gid = taskGroupMap[String(t.id)];
      if (gid && byGroup[gid]) { byGroup[gid].push(t); }
      else { ungrouped.push(t); }
    });
    var sections = [];
    sections.push({ group: null, tasks: ungrouped });
    groups.forEach(function (g) {
      sections.push({ group: g, tasks: byGroup[g.id] || [] });
    });
    return sections;
  }, [groups, displayTasks, taskGroupMap, isGroupPanelOpen, activeGroupTab]);

  // ── Compute uniform tag widths per column ─────────────────────────
  var columnMinWidths = useMemo(function () {
    var charW = 5.5; // approximate char width at 10px Raleway
    var pad = 14;     // padding (6+6) + border (2)

    // Status: measure the full badge text rendered in the PDF ("Label (100%)")
    // so the column is wide enough without overflow
    var statusMax = 0;
    STATUS_OPTIONS.forEach(function (o) {
      var badgeText = o.label + ' (100%)'; // worst-case percentage suffix
      if (badgeText.length > statusMax) statusMax = badgeText.length;
    });
    // Also account for "Overdue (0%)" label
    var overdueText = 'Overdue (0%)';
    if (overdueText.length > statusMax) statusMax = overdueText.length;

    // Priority: from options (11px font → wider chars)
    var priorityMax = 0;
    PRIORITY_OPTIONS.forEach(function (p) {
      if (p.value.length > priorityMax) priorityMax = p.value.length;
    });

    // Category: fixed width based on the longest name in the full CATEGORY_COLORS list
    // Does not change as tasks change — consistent column width for the project
    var catMax = 0;
    Object.keys(CATEGORY_COLORS).forEach(function (c) {
      if (c.length > catMax) catMax = c.length;
    });
    if (catMax === 0) catMax = 6;

    // Assigned To: width is based solely on users actually assigned to tasks in
    // THIS project — guarantees the column is per-project and never inflated by
    // HFB allocations or other-project data.
    var assignedMax = 0;
    var _taskAssignedIds = new Set();
    (tasksHook.tasks || []).forEach(function (t) {
      (t.assignedUserIds || []).forEach(function (id) { _taskAssignedIds.add(String(id)); });
    });
    _taskAssignedIds.forEach(function (id) {
      var name = getUserName(id);
      if (name && name !== '—' && name.length > assignedMax) assignedMax = name.length;
    });
    // Tag text width: 6.0px per char at Raleway 10px/400
    var assignedTagTextW = Math.ceil(assignedMax * 6.0);
    // Tag total width: text + 6px left pad + 4px gap (for longest name) + 12px X button + 4px right pad + 2px border = 28px overhead
    // With space-between: longest name gets exactly 4px gap; shorter names get more gap but X stays right-aligned
    var assignedTagW = Math.ceil((assignedTagTextW + 28) * 0.85);
    // Column width: tag + 8px left cell pad + 8px right cell pad = tag + 16
    var assignedColW = Math.max(80, assignedTagW + 16);

    return {
      status: Math.ceil(statusMax * charW + pad),
      priority: Math.ceil(priorityMax * 6.2 + 24),
      category: Math.ceil((catMax * charW + pad) * 0.9296),
      assigned: assignedColW,
      assignedTag: assignedTagW
    };
  }, [tasksHook.tasks, PRIORITY_OPTIONS, getUserName]);

  // ── Debounced progress updates (parent tasks) ─────────────────────
  var progressTimers = useRef({});

  var handleProgressChange = function (taskId, newProgress) {
    if (progressTimers.current[taskId]) {
      clearTimeout(progressTimers.current[taskId]);
    }
    progressTimers.current[taskId] = setTimeout(function () {
      tasksHook.updateTask(taskId, { progress: newProgress });
      delete progressTimers.current[taskId];
    }, 400);
  };

  // ── Add task handler ───────────────────────────────────────────────
  var [isAdding, setIsAdding] = useState(false);

  var handleAddTask = async function (progress, groupId) {
    setIsAdding(true);
    var taskData = { task: '' };
    if (progress != null) taskData.progress = progress;
    var result = await tasksHook.addTask(taskData);
    if (!result.success) {
      console.error('[ProjectTasks] Failed to add task:', result.error);
    } else if (result.data && groupId) {
      groupsHook.moveTaskToGroup(result.data.id, groupId);
    }
    setIsAdding(false);
  };

  // ── Delete task handler ────────────────────────────────────────────
  var handleDeleteTask = async function (taskId) {
    if (!canDelete) return;
    var result = await tasksHook.deleteTask(taskId);
    if (!result.success) {
      console.error('[ProjectTasks] Failed to delete task:', result.error);
      alert('Delete failed: ' + result.error);
    }
  };

  // ── Group management handlers ──────────────────────────────────────
  var handleAddGroup = useCallback(async function () {
    var color = GROUP_COLORS[groups.length % GROUP_COLORS.length];
    var result = await groupsHook.createGroup('Untitled Group', color, groups.length);
    if (result.success && result.data) {
      setEditingGroupId(result.data.id);
      setCollapsedGroupIds(function (prev) { return { ...prev, [result.data.id]: true }; });
    }
  }, [groups.length, groupsHook]);

  var toggleGroupCollapse = useCallback(function (groupId) {
    setCollapsedGroupIds(function (prev) {
      var next = { ...prev };
      if (next[groupId]) { delete next[groupId]; } else { next[groupId] = true; }
      return next;
    });
  }, []);

  var handleRenameGroup = useCallback(function (groupId, newName) {
    groupsHook.renameGroup(groupId, newName);
    setEditingGroupId(null);
  }, [groupsHook]);

  var handleDeleteGroup = useCallback(function (groupId) {
    groupsHook.deleteGroup(groupId);
    setActiveGroupTab(function (current) { return current === groupId ? 'all' : current; });
  }, [groupsHook]);

  var handleMoveTaskToGroup = useCallback(function (taskId, groupId) {
    groupsHook.moveTaskToGroup(taskId, groupId);
  }, [groupsHook]);

  var handleReorderGroups = useCallback(function (dragGroupId, dropGroupId) {
    if (dragGroupId === dropGroupId) return;
    var dragIdx = groups.findIndex(function (g) { return g.id === dragGroupId; });
    var dropIdx = groups.findIndex(function (g) { return g.id === dropGroupId; });
    if (dragIdx === -1 || dropIdx === -1) return;
    var next = [...groups];
    var removed = next.splice(dragIdx, 1)[0];
    next.splice(dropIdx, 0, removed);
    groupsHook.reorderGroups(next);
  }, [groups, groupsHook]);

  var handleGroupDragStart = useCallback(function (event) {
    setGroupDragActiveId(event.active.id);
  }, []);

  var handleGroupDragEnd = useCallback(function (event) {
    setGroupDragActiveId(null);
    var active = event.active;
    var over = event.over;
    if (!active || !over) return;
    var activeData = active.data.current || {};
    var overData = over.data.current || {};

    if (activeData.type === 'task-group-drag') {
      if (overData.type === 'group-drop') {
        handleMoveTaskToGroup(activeData.taskId, overData.groupId);
      } else if (overData.type === 'ungrouped-drop') {
        handleMoveTaskToGroup(activeData.taskId, null);
      } else if (overData.type === 'task-group-drag' && overData.taskId !== activeData.taskId) {
        // Determine whether both tasks belong to the same group.
        // If same group → reorder the sequence.
        // If different groups → assign the dragged task to the target task's group
        //   (prevents a task from "replacing" a position in a different group).
        var activeGroupId = taskGroupMap[String(activeData.taskId)] || null;
        var overGroupId = taskGroupMap[String(overData.taskId)] || null;
        if (activeGroupId === overGroupId) {
          tasksHook.reorderTasks(activeData.taskId, overData.taskId);
          setIsManualOrder(true);
        } else {
          handleMoveTaskToGroup(activeData.taskId, overGroupId);
        }
      }
      return;
    }
    if (activeData.type === 'group-reorder' && overData.type === 'group-drop') {
      handleReorderGroups(activeData.groupId, overData.groupId);
    }
  }, [handleMoveTaskToGroup, handleReorderGroups, tasksHook, taskGroupMap]);

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════

  var hasGroups = groups.length > 0;
  var parentColCount = canDelete ? 10 : 9; // # + Task + Category + Assigned + Priority + Start + Due + DailyUpdate + Progress + (Delete)

  var containerStyle = isFullscreen
    ? {
        fontFamily: 'Raleway, sans-serif',
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        backgroundColor: 'white',
        overflow: 'auto',
        padding: '16px 24px'
      }
    : { fontFamily: 'Raleway, sans-serif', maxWidth: '100%' };

  return (
    <div style={containerStyle}>

      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontSize: '13px', fontWeight: 200, fontStyle: 'italic', color: BRIO_GREY, margin: 0, marginTop: '30px' }}>
          Weekly to-do list per project — assign tasks, track progress
        </p>
      </div>

      {/* ── Project Selector ────────────────────────────────────────── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px',
        marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid ' + BRIO_BORDER
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '12px', fontWeight: 400, color: BRIO_GREY, whiteSpace: 'nowrap' }}>
            Project
          </label>
          <select
            value={selectedProjectId || ''}
            onChange={function (e) { setSelectedProjectId(e.target.value ? Number(e.target.value) : null); }}
            style={{
              fontFamily: 'Raleway, sans-serif', fontSize: '13px', fontWeight: 300,
              padding: '6px 28px 6px 10px', borderRadius: '0',
              border: '1px solid ' + BRIO_BORDER, background: '#fff',
              color: '#333', cursor: 'pointer', minWidth: '220px',
              outline: 'none', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'10\' height=\'6\'%3E%3Cpath d=\'M0 0l5 6 5-6z\' fill=\'%23999\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center'
            }}
          >
            {sortedProjects.length === 0 && <option value="">No projects available</option>}
            {sortedProjects.map(function (p) {
              return (
                <option key={p.id} value={p.id}>
                  {p.projectCode ? `${p.projectCode} \u2014 ${p.name}` : p.name}
                </option>
              );
            })}
          </select>
        </div>

        <button onClick={handleTodayClick} style={{
          fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 500,
          padding: '5px 12px', borderRadius: '0',
          border: '1px solid ' + BRIO_GOLD,
          background: currentWeekId === getCurrentWeekId() ? BRIO_GOLD : 'transparent',
          color: currentWeekId === getCurrentWeekId() ? '#fff' : BRIO_GOLD,
          cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap'
        }}>
          Today
        </button>

        {tasksHook.tasks.length > 0 && (
          <span style={{ fontSize: '11px', fontWeight: 400, color: BRIO_GREY, marginLeft: 'auto' }}>
            {filterRules.length > 0
              ? displayTasks.length + ' of ' + tasksHook.tasks.length + ' task' + (tasksHook.tasks.length !== 1 ? 's' : '') + ' shown'
              : tasksHook.tasks.length + ' task' + (tasksHook.tasks.length !== 1 ? 's' : '')}
            {tasksHook.tasks.filter(function (t) { return t.isCarriedForward; }).length > 0 && (
              <span style={{ color: BRIO_ORANGE, marginLeft: '6px' }}>
                ({tasksHook.tasks.filter(function (t) { return t.isCarriedForward; }).length} carried)
              </span>
            )}
            {tasksHook.tasks.filter(function (t) { return t.progress === 100; }).length > 0 && (
              <span style={{ color: BRIO_GREEN, marginLeft: '6px' }}>
                ({tasksHook.tasks.filter(function (t) { return t.progress === 100; }).length} done)
              </span>
            )}
          </span>
        )}

        {/* Fullscreen toggle */}
        <button
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '24px', height: '24px', border: 'none', borderRadius: '3px',
            background: 'transparent', cursor: 'pointer',
            color: isFullscreen ? BRIO_GOLD : BRIO_GREY,
            marginLeft: tasksHook.tasks.length > 0 ? '8px' : 'auto',
            transition: 'color 0.15s'
          }}
          onMouseEnter={function (e) { e.currentTarget.style.color = BRIO_GOLD; }}
          onMouseLeave={function (e) { e.currentTarget.style.color = isFullscreen ? BRIO_GOLD : BRIO_GREY; }}
        >
          {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
        </button>
      </div>

      {/* ── Week Tab Bar ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '20px', overflowX: 'auto' }}>
        <button onClick={handlePrevWeeks} title="Earlier weeks" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '28px', height: '28px', borderRadius: '0',
          border: '1px solid ' + BRIO_BORDER, background: '#fff',
          cursor: 'pointer', flexShrink: 0, color: BRIO_GREY
        }}>
          <ChevronLeft size={14} />
        </button>

        {weekTabs.map(function (wid) {
          var isActive = wid === currentWeekId;
          var isToday = wid === getCurrentWeekId();
          return (
            <button key={wid} onClick={function () { handleSelectWeek(wid); }} style={{
              fontFamily: 'Raleway, sans-serif', fontSize: '11px',
              fontWeight: isActive ? 600 : 300, padding: '6px 12px', borderRadius: '0',
              border: isActive ? '1px solid ' + BRIO_GOLD : '1px solid transparent',
              background: isActive ? BRIO_GOLD + '10' : 'transparent',
              color: isActive ? BRIO_GOLD : (isToday ? BRIO_GREEN : '#555'),
              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s ease',
              position: 'relative'
            }}>
              <div>{wid}</div>
              <div style={{ fontSize: '9px', fontWeight: 200, color: isActive ? BRIO_GOLD : '#999', marginTop: '1px' }}>
                {weekIdToDateRange(wid)}
              </div>
              {isToday && !isActive && (
                <div style={{
                  position: 'absolute', bottom: '-1px', left: '50%', transform: 'translateX(-50%)',
                  width: '4px', height: '4px', borderRadius: '50%', background: BRIO_GREEN
                }} />
              )}
            </button>
          );
        })}

        <button onClick={handleNextWeeks} title="Later weeks" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '28px', height: '28px', borderRadius: '0',
          border: '1px solid ' + BRIO_BORDER, background: '#fff',
          cursor: 'pointer', flexShrink: 0, color: BRIO_GREY
        }}>
          <ChevronRight size={14} />
        </button>
      </div>

      {/* ── Error state ─────────────────────────────────────────────── */}
      {tasksHook.error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '12px 16px', borderRadius: '0',
          background: '#FFEBEE', border: '1px solid #FFCDD2',
          marginBottom: '16px', fontSize: '13px', fontWeight: 300, color: BRIO_RED
        }}>
          <AlertCircle size={16} />
          {tasksHook.error}
        </div>
      )}

      {/* ── Loading state ───────────────────────────────────────────── */}
      {tasksHook.isLoading && (
        <div style={{ textAlign: 'center', padding: '40px 20px', fontSize: '13px', fontWeight: 300, color: BRIO_GREY }}>
          Loading tasks…
        </div>
      )}

      {/* ── No project selected ─────────────────────────────────────── */}
      {!selectedProjectId && !tasksHook.isLoading && (
        <div style={{ textAlign: 'center', padding: '60px 20px', fontSize: '13px', fontWeight: 300, color: '#aaa' }}>
          Select a project to view and manage tasks.
        </div>
      )}

      {/* ── Tasks Table ─────────────────────────────────────────────── */}
      {selectedProjectId && !tasksHook.isLoading && (
        <div style={{
          background: '#fff', borderRadius: '0',
          border: '1px solid ' + BRIO_BORDER, overflow: 'visible'
        }}>
          <DndContext
            sensors={groupDndSensors}
            collisionDetection={closestCenter}
            onDragStart={handleGroupDragStart}
            onDragEnd={handleGroupDragEnd}
          >
          {/* ── Toolbar: new task left, sort/filter right ──────── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '2px', padding: '5px 12px',
            borderBottom: '1px solid ' + BRIO_BORDER, background: BRIO_BG
          }}>
            {/* New Task button — left side */}
            {canEdit && (
              <ToolbarIcon
                onClick={function () { handleAddTask(null, activeGroupTab !== 'all' && activeGroupTab !== 'ungrouped' ? activeGroupTab : null); }}
                disabled={isAdding || !selectedProjectId}
                tooltip="New task"
                active={false}
              >
                <Plus size={11} />
              </ToolbarIcon>
            )}

            {/* New group button — only for editors */}
            {canEdit && (
              <ToolbarIcon
                onClick={handleAddGroup}
                disabled={!selectedProjectId}
                tooltip="New group"
                active={false}
              >
                <Layers size={11} />
              </ToolbarIcon>
            )}
            {/* Keyboard / interaction hints tooltip */}
            <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
              onMouseEnter={function (e) { e.currentTarget.querySelector('[data-hints-tip]').style.display = 'block'; }}
              onMouseLeave={function (e) { e.currentTarget.querySelector('[data-hints-tip]').style.display = 'none'; }}
            >
              <Info size={11} strokeWidth={1.5} style={{ color: '#bbb', cursor: 'default' }} />
              <span data-hints-tip="1" style={{
                display: 'none', position: 'absolute', bottom: '100%', left: '50%',
                transform: 'translateX(-50%)', marginBottom: '6px', padding: '8px 12px',
                borderRadius: '4px', background: '#333', color: '#fff', fontSize: '9px', fontWeight: 400,
                whiteSpace: 'pre', pointerEvents: 'none', zIndex: 400,
                lineHeight: '16px', fontFamily: 'Raleway, sans-serif'
              }}>{'drag and drop: long press on task number\nnext line in cell: alt + enter\nbullets: (1.) > enter text or space > alt + enter\nbullets: (-) > enter text or space > alt + enter'}</span>
            </span>

            {/* Group navigation — visible to all users */}
            {groups.length > 0 && (
              <button
                onClick={function () { setActiveGroupTab('all'); }}
                title="Return to default view (all groups)"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', background: 'transparent', padding: '3px 2px',
                  cursor: 'pointer', color: activeGroupTab === 'all' ? BRIO_GOLD : '#bbb',
                  transition: 'color 0.15s', lineHeight: 1
                }}
              >
                <Home size={11} />
              </button>
            )}
            {groups.length > 0 && (
              <button
                onClick={function () { setIsGroupPanelOpen(function (v) { return !v; }); }}
                title={isGroupPanelOpen ? 'Hide groups' : 'Show groups'}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', background: 'transparent', padding: '3px 2px',
                  cursor: 'pointer', color: isGroupPanelOpen ? BRIO_GOLD : '#bbb',
                  transition: 'color 0.15s', lineHeight: 1
                }}
              >
                <ChevronRight
                  size={11}
                  style={{
                    transform: isGroupPanelOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                />
              </button>
            )}
            {/* Inline group tabs — visible to all users */}
            {isGroupPanelOpen && groups.length > 0 && (
              <>
                <div style={{ width: '1px', height: '16px', background: BRIO_BORDER, margin: '0 4px', flexShrink: 0 }} />
                {/* Group tabs info tooltip */}
                <span
                  style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginRight: '4px', cursor: 'default', flexShrink: 0 }}
                  onMouseEnter={function (e) { e.currentTarget.querySelector('[data-grouptab-tip]').style.display = 'block'; }}
                  onMouseLeave={function (e) { e.currentTarget.querySelector('[data-grouptab-tip]').style.display = 'none'; }}
                >
                  <Info size={11} style={{ color: '#bbb' }} />
                  <span data-grouptab-tip="1" style={{
                    display: 'none', position: 'absolute', bottom: '100%', left: '50%',
                    transform: 'translateX(-50%)', marginBottom: '6px', padding: '6px 10px',
                    background: '#333', color: '#fff', fontSize: '9px', fontWeight: 400,
                    borderRadius: '3px', whiteSpace: 'normal', pointerEvents: 'none', zIndex: 400,
                    lineHeight: '14px', fontFamily: 'Raleway, sans-serif', textTransform: 'none',
                    width: '200px', textAlign: 'center'
                  }}>
                    Default view shows all groups. Select group tab to view group tasks, unselect group tab to return to default view. Use expanding triangle to view all group tabs. Works similarly in status tracker.
                  </span>
                </span>
                {groups.map(function (group) {
                  return (
                    <GroupTab
                      key={group.id}
                      group={group}
                      isActive={activeGroupTab === group.id}
                      isEditing={editingGroupId === group.id}
                      onSelect={function () { setActiveGroupTab(activeGroupTab === group.id ? 'all' : group.id); }}
                      onStartEdit={function () { setEditingGroupId(group.id); }}
                      onFinishEdit={function (name) { handleRenameGroup(group.id, name); }}
                      onDelete={function () { handleDeleteGroup(group.id); }}
                    />
                  );
                })}
              </>
            )}

            {/* Board-mode hint icon — only visible in status tracker view */}
            {viewMode === 'board' && (
              <span
                style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '2px', cursor: 'default' }}
                onMouseEnter={function (e) { e.currentTarget.querySelector('[data-board-tip]').style.display = 'block'; }}
                onMouseLeave={function (e) { e.currentTarget.querySelector('[data-board-tip]').style.display = 'none'; }}
              >
                <Info size={11} style={{ color: '#bbb' }} />
                <span data-board-tip="1" style={{
                  display: 'none', position: 'absolute', bottom: '100%', left: '50%',
                  transform: 'translateX(-50%)', marginBottom: '6px', padding: '6px 10px',
                  background: '#333', color: '#fff', fontSize: '9px', fontWeight: 400,
                  borderRadius: '3px', whiteSpace: 'normal', pointerEvents: 'none', zIndex: 400,
                  lineHeight: '14px', fontFamily: 'Raleway, sans-serif', textTransform: 'none',
                  width: '160px', textAlign: 'center'
                }}>
                  Select and drag dots on the right side of task card to move between categories
                </span>
              </span>
            )}

            {/* Spacer pushes sort/filter to right */}
            <div style={{ flex: 1 }} />

            {/* Sort button (frameless) */}
            <div style={{ position: 'relative' }}>
              <ToolbarIcon
                onClick={function () { setShowSortPanel(!showSortPanel); setShowFilterPanel(false); }}
                tooltip={'Sort' + (sortRules.length > 0 ? ' (' + sortRules.length + ')' : '')}
                active={showSortPanel || sortRules.length > 0}
                badge={sortRules.length > 0 ? sortRules.length : null}
                frameless
              >
                <ArrowUpDown size={11} />
              </ToolbarIcon>
              {showSortPanel && (
                <SortPanel
                  sortRules={sortRules}
                  onChangeSortRules={function (rules) { setSortRules(rules); setIsManualOrder(false); }}
                  onSaveForEveryone={function () { saveForEveryone({ clearAllPersonal: isAdmin || isSupervisor }); }}
                  onSaveForMe={saveForMe}
                  onClose={closeSortPanel}
                  canSaveForEveryone={isAdmin || isSupervisor}
                />
              )}
            </div>

            {/* Filter button (frameless) */}
            <div style={{ position: 'relative' }}>
              <ToolbarIcon
                onClick={function () { setShowFilterPanel(!showFilterPanel); setShowSortPanel(false); }}
                tooltip={'Filter' + (filterRules.length > 0 ? ' (' + filterRules.length + ')' : '')}
                active={showFilterPanel || filterRules.length > 0}
                badge={filterRules.length > 0 ? filterRules.length : null}
                frameless
              >
                <Filter size={11} />
              </ToolbarIcon>
              {showFilterPanel && (
                <FilterPanel
                  filterRules={filterRules}
                  onChangeFilterRules={setFilterRules}
                  onSaveForEveryone={function () { saveForEveryone({ clearAllPersonal: isAdmin || isSupervisor }); }}
                  onSaveForMe={saveForMe}
                  onClose={closeFilterPanel}
                  priorityOptions={PRIORITY_OPTIONS}
                  getUserName={getUserName}
                  allUsers={allocatedUsers}
                  taskCategories={TASK_CATEGORIES}
                  canSaveForEveryone={isAdmin || isSupervisor}
                />
              )}
            </div>

            {/* Status Tracker toggle (frameless) */}
            <ToolbarIcon
              onClick={function () { setViewMode(viewMode === 'table' ? 'board' : 'table'); }}
              tooltip={viewMode === 'table' ? 'Status tracker' : 'Table view'}
              active={viewMode === 'board'}
              frameless
            >
              <TrendingUp size={11} />
            </ToolbarIcon>

            {/* Zoom controls — ZoomOut | % label (double-click resets) | ZoomIn */}
            <ToolbarIcon onClick={function () { setPageZoomIndex(function (i) { return Math.max(i - 1, 0); }); }} tooltip="Zoom out" frameless>
              <ZoomOut size={11} />
            </ToolbarIcon>
            <ZoomResetLabel
              zoomLabel={PAGE_ZOOM_LEVELS[pageZoomIndex].label}
              onReset={function () { setPageZoomIndex(PAGE_ZOOM_DEFAULT); }}
            />
            <ToolbarIcon onClick={function () { setPageZoomIndex(function (i) { return Math.min(i + 1, PAGE_ZOOM_LEVELS.length - 1); }); }} tooltip="Zoom in" frameless>
              <ZoomIn size={11} />
            </ToolbarIcon>

            {/* Settings (import / export) */}
            <div ref={settingsPanelRef} style={{ position: 'relative' }}>
              <ToolbarIcon
                onClick={function () { setShowSettingsPanel(!showSettingsPanel); setShowSortPanel(false); setShowFilterPanel(false); }}
                tooltip="Settings"
                active={showSettingsPanel}
                frameless
              >
                <Settings2 size={11} />
              </ToolbarIcon>

              {showSettingsPanel && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 300,
                  background: '#fff', border: '1px solid ' + BRIO_BORDER,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.10)', borderRadius: '3px',
                  minWidth: '170px', fontFamily: 'Raleway, sans-serif'
                }}>
                  {/* ── Export ── */}
                  <div style={{ padding: '6px 12px 4px', fontSize: '9px', fontWeight: 600, color: '#aaa', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid ' + BRIO_BORDER, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Download size={9} /> Export
                  </div>
                  {[
                    { label: 'Export as PDF', icon: <FileText size={11} />, action: exportPDF },
                    { label: 'Export as Excel', icon: <FileSpreadsheet size={11} />, action: exportExcel },
                    { label: 'Export as CSV', icon: <File size={11} />, action: exportCSV }
                  ].map(function (item) {
                    return (
                      <button key={item.label} onClick={item.action} style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                        padding: '7px 14px', border: 'none', background: 'transparent',
                        fontSize: '11px', fontWeight: 300, color: '#444', cursor: 'pointer',
                        fontFamily: 'Raleway, sans-serif', textAlign: 'left',
                        transition: 'background 0.1s'
                      }}
                        onMouseEnter={function (e) { e.currentTarget.style.background = BRIO_BG; }}
                        onMouseLeave={function (e) { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {item.icon}{item.label}
                      </button>
                    );
                  })}

                  {/* ── Import ── */}
                  <div style={{ padding: '6px 12px 4px', fontSize: '9px', fontWeight: 600, color: '#aaa', letterSpacing: '0.06em', textTransform: 'uppercase', borderTop: '1px solid ' + BRIO_BORDER, borderBottom: '1px solid ' + BRIO_BORDER, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Upload size={9} /> Import
                  </div>
                  {[
                    { label: 'Import from Excel', icon: <FileSpreadsheet size={11} />, action: function () { importXlsxRef.current && importXlsxRef.current.click(); } },
                    { label: 'Import from CSV', icon: <File size={11} />, action: function () { importCsvRef.current && importCsvRef.current.click(); } }
                  ].map(function (item) {
                    return (
                      <button key={item.label} onClick={item.action} style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                        padding: '7px 14px', border: 'none', background: 'transparent',
                        fontSize: '11px', fontWeight: 300, color: '#444', cursor: 'pointer',
                        fontFamily: 'Raleway, sans-serif', textAlign: 'left',
                        transition: 'background 0.1s'
                      }}
                        onMouseEnter={function (e) { e.currentTarget.style.background = BRIO_BG; }}
                        onMouseLeave={function (e) { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {item.icon}{item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Hidden file inputs for import */}
            <input ref={importCsvRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleImportCSV} />
            <input ref={importXlsxRef} type="file" accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" style={{ display: 'none' }} onChange={handleImportExcel} />
          </div>


          {/* ── Main content area ─────────────────────────────────────── */}
          <div style={{ minWidth: 0 }}>


              {/* ── Board View ───────────────────────────────────────── */}
              {viewMode === 'board' && (
              <div style={{ zoom: PAGE_ZOOM_LEVELS[pageZoomIndex].scale }}>
                <StatusTrackerBoard
                  tasks={displayTasks}
                  groupedDisplaySections={groupedDisplaySections}
                  collapsedGroupIds={collapsedGroupIds}
                  onToggleGroupCollapse={function (groupId) {
                    setCollapsedGroupIds(function (prev) {
                      var next = Object.assign({}, prev);
                      if (next[groupId]) { delete next[groupId]; } else { next[groupId] = true; }
                      return next;
                    });
                  }}
                  getUserName={getUserName}
                  getUserColor={getUserColor}
                  onUpdateProgress={function (taskId, progress) { handleProgressChange(taskId, progress); }}
                  onUpdateTask={function (taskId, fields) { tasksHook.updateTask(taskId, fields); }}
                  onAddTask={function (progress) { handleAddTask(progress); }}
                  onMoveTaskToGroup={handleMoveTaskToGroup}
                  canEdit={canEdit}
                />
              </div>
              )}

              {/* ── Table View ──────────────────────────────────────── */}
              {viewMode === 'table' && <div style={{ overflow: 'auto', WebkitOverflowScrolling: 'touch', zoom: PAGE_ZOOM_LEVELS[pageZoomIndex].scale }}>
            <SortableContext items={displayTasks.map(function (t) { return 'task-drag-' + t.id; })} strategy={verticalListSortingStrategy}>
            <table style={{
              width: '100%', minWidth: '820px', borderCollapse: 'collapse',
              tableLayout: 'fixed',
              fontSize: '13px', fontWeight: 300, fontFamily: 'Raleway, sans-serif',
              userSelect: 'none', WebkitUserSelect: 'none',
            }}>
              <thead>
                {/* With tableLayout:'fixed' each th width is the authoritative column width.
                    The Task column has no explicit width so it absorbs all remaining space. */}
                <tr style={{ background: BRIO_BG }}>
                  <th style={{ ...thStyle, width: '32px', textAlign: 'center', padding: '0 4px', display: 'table-cell', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Hash size={11} style={{ color: '#bbb' }} />
                    </div>
                  </th>
                  <th style={{ ...thStyle }}>Task</th>
                  <th style={{ ...thStyle, width: '116px', whiteSpace: 'nowrap', textAlign: 'center' }}>Category</th>
                  <th style={{ ...thStyle, width: columnMinWidths.assigned + 'px', whiteSpace: 'nowrap', textAlign: 'center', padding: '10px 8px' }}>Assigned To</th>
                  <th style={{ ...thStyle, width: '80px',  whiteSpace: 'nowrap', textAlign: 'center' }}>Priority</th>
                  <th style={{ ...thStyle, width: '86px',  whiteSpace: 'nowrap', textAlign: 'center' }}>Start</th>
                  <th style={{ ...thStyle, width: '86px',  whiteSpace: 'nowrap', textAlign: 'center' }}>Due</th>
                  <th style={{ ...thStyle, width: '250px' }}>Daily Update</th>
                  <th style={{ ...thStyle, width: (displayTasks.some(function(t) { return isOverdueCheck(t.completionDate, t.progress); }) ? PROGRESS_COL_WITH_ICON : PROGRESS_COL_NO_ICON) + 'px', whiteSpace: 'nowrap', textAlign: 'center', borderRight: canDelete ? thStyle.borderRight : 'none' }}>Progress</th>
                  {canDelete && <th style={{ ...thStyle, width: '36px', whiteSpace: 'nowrap', borderRight: 'none' }}></th>}
                </tr>
              </thead>
              {displayTasks.length === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={parentColCount} style={{
                      textAlign: 'center', padding: '40px 20px', color: '#bbb',
                      fontSize: '13px', fontWeight: 300, fontStyle: 'italic'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        <CheckCircle2 size={24} style={{ color: '#ddd' }} />
                      </div>
                      {tasksHook.tasks.length > 0 && filterRules.length > 0
                        ? 'No tasks match the current filters'
                        : 'No tasks for ' + (selectedProject ? selectedProject.name : 'this project') + ' in ' + currentWeekId}
                    </td>
                  </tr>
                </tbody>
              )}

                {groupedDisplaySections.reduce(function (acc, section) {
                  // Compute running serial offset: sum of visible tasks in prior sections
                  var offset = acc.offset;
                  acc.sections.push({ section: section, serialOffset: offset });
                  var group = section.group;
                  var isCollapsed = group ? !!collapsedGroupIds[group.id] : false;
                  acc.offset += (!group || !isCollapsed) ? section.tasks.length : 0;
                  return acc;
                }, { offset: 0, sections: [] }).sections.map(function (entry, sectionIdx) {
                  var section = entry.section;
                  var serialOffset = entry.serialOffset;
                  var group = section.group;
                  var sectionTasks = section.tasks;
                  var isCollapsed = group ? !!collapsedGroupIds[group.id] : false;
                  var groupColor = group ? (group.color || BRIO_GOLD) : null;
                  var activeDragTaskId = groupDragActiveId && groupDragActiveId.startsWith('task-drag-')
                    ? groupDragActiveId.replace('task-drag-', '') : null;

                  var renderTaskRow = function (task, indexInSection) {
                    var serialNumber = indexInSection + 1;
                    var taskOverdue = isOverdueCheck(task.completionDate, task.progress);
                    return (
                      <SortableTaskRow
                        key={task.id}
                        taskId={task.id}
                        taskGroupMap={taskGroupMap}
                        activeDragTaskId={activeDragTaskId}
                        disabled={!canEdit}
                        style={{
                          borderTop: '1px solid ' + BRIO_BORDER,
                          background: taskOverdue ? '#6B202008' : (task.progress === 100 ? '#68958108' : '#fff'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {function (dragListeners) { return (
                        <>
                        {/* Serial number — drag handle: long-press to activate */}
                        <td
                          style={{
                            ...tdStyle, width: '32px', textAlign: 'center', padding: '0 4px',
                            color: '#bbb', fontSize: '11px', fontWeight: 300,
                            userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
                            fontFamily: 'Raleway, sans-serif',
                            cursor: canEdit ? 'grab' : 'default',
                            touchAction: canEdit ? 'none' : 'auto',
                          }}
                          title={canEdit ? 'long press on task number for drag and drop' : ''}
                          {...(canEdit ? dragListeners : {})}
                        >
                          {serialNumber}
                        </td>

                        {/* Task name */}
                        {(() => {
                          const isTimelineLinked = !!(task.categories && task.categories.includes('timeline_linked'));
                          const isMomLinked = !!(task.categories && task.categories.includes('MOM_Linked'));
                          const hasDisc = isTimelineLinked || isMomLinked;
                          return (
                          <td style={{ ...tdStyle, verticalAlign: 'top', fontSize: '13px', position: 'relative', paddingRight: hasDisc ? '12px' : '4px' }}>
                            {hasDisc && (
                              <span style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', flexDirection: 'column', gap: '3px', pointerEvents: 'none' }}>
                                {isTimelineLinked && <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: BRIO_GOLD, display: 'inline-block' }} />}
                                {isMomLinked && <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#6B5EA8', display: 'inline-block' }} />}
                              </span>
                            )}
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                            <div style={{ flex: 1 }}>
                              <InlineTextCell
                                value={task.task}
                                placeholder="Task name…"
                                onChange={function (val) { tasksHook.updateTask(task.id, { task: val }); }}
                                style={task.progress === 100 ? { textDecoration: 'line-through', color: '#999' } : {}}
                                readOnly={!canEdit}
                                autoClearDefaults
                              />
                            </div>
                            {task.isCarriedForward && (
                              <span
                                style={{
                                  position: 'relative', display: 'inline-flex', alignItems: 'center',
                                  color: BRIO_ORANGE, cursor: 'default', flexShrink: 0, marginTop: '6px'
                                }}
                                onMouseEnter={function (e) {
                                  var tip = e.currentTarget.querySelector('[data-cf-tip]');
                                  if (tip) tip.style.display = 'block';
                                }}
                                onMouseLeave={function (e) {
                                  var tip = e.currentTarget.querySelector('[data-cf-tip]');
                                  if (tip) tip.style.display = 'none';
                                }}
                              >
                                <RotateCcw size={9} />
                                <span data-cf-tip="1" style={{
                                  display: 'none', position: 'absolute', bottom: '100%', left: '50%',
                                  transform: 'translateX(-50%)', marginBottom: '4px', padding: '4px 8px',
                                  background: '#333', color: '#fff', fontSize: '9px', fontWeight: 400,
                                  borderRadius: '3px', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 400,
                                  lineHeight: '14px', fontFamily: 'Raleway, sans-serif'
                                }}>Carried from {task.originWeekId}</span>
                              </span>
                            )}
                          </div>
                        </td>
                          );
                        })()}

                        {/* Category */}
                        <td style={{ ...tdStyle, ...tdCompact, paddingLeft: '6px', paddingRight: '6px' }}>
                          <CategoryMultiSelect
                            value={task.categories}
                            onChange={function (val) { tasksHook.updateTask(task.id, { categories: val }); }}
                            readOnly={!canEdit}
                            tagMinWidth={columnMinWidths.category}
                            taskCategories={TASK_CATEGORIES}
                            categoryColors={CATEGORY_COLORS}
                          />
                        </td>

                        {/* Assigned To */}
                        <td style={{ ...tdStyle, ...tdCompact, overflow: 'hidden', paddingLeft: '8px', paddingRight: '8px' }}>
                          <AssignedMultiSelect
                            value={task.assignedUserIds}
                            allocatedUsers={allocatedUsers}
                            getUserName={getUserName}
                            getUserColor={getUserColor}
                            allUsers={allocatedUsers}
                            tagMinWidth={columnMinWidths.assignedTag}
                            onChange={function (val) { tasksHook.updateTask(task.id, { assignedUserIds: val }); }}
                            readOnly={!canEdit}
                          />
                        </td>

                        {/* Priority */}
                        <td style={{ ...tdStyle, ...tdCompact, textAlign: 'center' }}>
                          <PrioritySelect
                            value={task.priority}
                            onChange={function (val) { tasksHook.updateTask(task.id, { priority: val }); }}
                            readOnly={!canEdit}
                            options={PRIORITY_OPTIONS}
                            tagMinWidth={columnMinWidths.priority}
                          />
                        </td>

                        {/* Start Date */}
                        <td style={{ ...tdStyle, ...tdCompact, paddingRight: '2px' }}>
                          <InlineDateCell
                            value={task.startDate}
                            onChange={function (val) { tasksHook.updateTask(task.id, { startDate: val }); }}
                            readOnly={!canEdit}
                          />
                        </td>

                        {/* Completion Date */}
                        <td style={{ ...tdStyle, ...tdCompact, paddingRight: '2px' }}>
                          <InlineDateCell
                            value={task.completionDate}
                            onChange={function (val) { tasksHook.updateTask(task.id, { completionDate: val }); }}
                            readOnly={!canEdit}
                          />
                        </td>

                        {/* Daily Update */}
                        <td style={{ ...tdStyle, width: '200px', verticalAlign: 'top', fontSize: '11px' }}>
                          <InlineTextCell
                            value={task.updates5pm}
                            placeholder="Update…"
                            onChange={function (val) { tasksHook.updateTask(task.id, { updates5pm: val }); }}
                            readOnly={!canEdit}
                            style={{ fontSize: '11px' }}
                          />
                        </td>

                        {/* Progress */}
                        <td style={{ ...tdStyle, ...tdCompact, borderRight: canDelete ? tdStyle.borderRight : 'none' }}>
                          <StatusSelect
                            value={task.progress}
                            completionDate={task.completionDate}
                            onChange={function (val) { handleProgressChange(task.id, val); }}
                            readOnly={!canEdit}
                            tagMinWidth={STATUS_TAG_MIN_WIDTH}
                          />
                        </td>

                        {/* Delete */}
                        {canDelete && (
                          <td style={{ ...tdStyle, ...tdCompact, textAlign: 'center', borderRight: 'none' }}>
                            <DeleteButton
                              onClick={function () { handleDeleteTask(task.id); }}
                              title="Delete task"
                            />
                          </td>
                        )}
                        </>
                        ); }}
                      </SortableTaskRow>
                    );
                  };

                  return (
                    <GroupTbodyDrop
                      key={group ? group.id : 'ungrouped-section'}
                      groupId={group ? group.id : null}
                      groupColor={groupColor}
                    >
                      {/* Ungrouped section header row — droppable */}
                      {/* Ungrouped tasks have no header — they render directly */}
                      {/* Group header row */}
                      {group && (
                        <tr style={{
                          borderTop: sectionIdx > 0 ? '2px solid ' + BRIO_BORDER : '1px solid ' + BRIO_BORDER,
                          background: groupColor + '08'
                        }}>
                          <td colSpan={parentColCount} style={{ padding: '5px 12px' }}>
                            <TableGroupHeaderDrop groupId={group.id}>
                              <button
                                onClick={function () { toggleGroupCollapse(group.id); }}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', border: 'none',
                                  background: 'transparent', padding: 0, cursor: 'pointer',
                                  color: groupColor, transition: 'transform 0.2s ease',
                                  transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
                                }}
                              >
                                <ChevronDown size={12} />
                              </button>
                              <span style={{
                                width: '7px', height: '7px', borderRadius: '50%',
                                background: groupColor, flexShrink: 0
                              }} />
                              {editingGroupId === group.id ? (
                                <input
                                  autoFocus
                                  defaultValue={group.name}
                                  onBlur={function (e) { handleRenameGroup(group.id, e.target.value); }}
                                  onKeyDown={function (e) {
                                    if (e.key === 'Enter') { handleRenameGroup(group.id, e.target.value); }
                                    if (e.key === 'Escape') { setEditingGroupId(null); }
                                  }}
                                  style={{
                                    fontSize: '12px', fontWeight: 500, color: groupColor,
                                    fontFamily: 'Raleway, sans-serif', border: 'none',
                                    borderBottom: '1px solid ' + groupColor,
                                    background: 'transparent', outline: 'none', padding: 0,
                                    width: Math.max(60, group.name.length * 8) + 'px'
                                  }}
                                />
                              ) : (
                                <span
                                  onDoubleClick={function () { setEditingGroupId(group.id); }}
                                  style={{
                                    fontSize: '12px', fontWeight: 500, color: groupColor,
                                    fontFamily: 'Raleway, sans-serif', cursor: 'default'
                                  }}
                                >
                                  {group.name}
                                </span>
                              )}
                              <span style={{
                                fontSize: '10px', fontWeight: 300, color: '#aaa',
                                fontFamily: 'Raleway, sans-serif'
                              }}>
                                ({sectionTasks.length})
                              </span>
                            </TableGroupHeaderDrop>
                          </td>
                        </tr>
                      )}
                      {/* Tasks in this section */}
                      {(!group || !isCollapsed) && sectionTasks.map(renderTaskRow)}
                      {/* "+ Add Task" row for this group */}
                      {group && !isCollapsed && canEdit && (
                        <tr style={{ borderTop: '1px solid ' + BRIO_BORDER }}>
                          <td colSpan={parentColCount} style={{ padding: '4px 12px 4px 38px' }}>
                            <button
                              onClick={function () { handleAddTask(null, group.id); }}
                              disabled={isAdding || !selectedProjectId}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                border: 'none', background: 'transparent', padding: '3px 0',
                                fontSize: '10px', fontWeight: 300, color: '#bbb',
                                cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
                                transition: 'color 0.15s'
                              }}
                              onMouseEnter={function (e) { e.currentTarget.style.color = groupColor; }}
                              onMouseLeave={function (e) { e.currentTarget.style.color = '#bbb'; }}
                            >
                              <Plus size={9} />
                              Add task to {group.name}
                            </button>
                          </td>
                        </tr>
                      )}
                    </GroupTbodyDrop>
                  );
                })}
            </table>
            </SortableContext>
              </div>}

          </div>{/* end main content */}

          {/* DragOverlay — floating visual that follows the cursor for all drag types */}
          <DragOverlay dropAnimation={null}>
            {(function () {
              if (!groupDragActiveId) return null;

              // Group-tab reorder drag
              if (groupDragActiveId.startsWith('group-drag-')) {
                return (
                  <div style={{
                    padding: '3px 10px', fontSize: '11px', fontWeight: 500,
                    fontFamily: 'Raleway, sans-serif', background: '#fff',
                    border: '1px solid ' + BRIO_GOLD, borderRadius: '3px',
                    color: BRIO_GOLD, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    pointerEvents: 'none'
                  }}>
                    {(groups.find(function (g) { return 'group-drag-' + g.id === groupDragActiveId; }) || {}).name || 'Group'}
                  </div>
                );
              }

              // Task drag — show a compact floating card with the task name
              if (groupDragActiveId.startsWith('task-drag-')) {
                var dragTaskId = Number(groupDragActiveId.replace('task-drag-', ''));
                var dragTask = displayTasks.find(function (t) { return t.id === dragTaskId; });
                if (dragTask) {
                  return (
                    <div style={{
                      padding: '4px 12px', fontSize: '12px', fontWeight: 400,
                      fontFamily: 'Raleway, sans-serif', background: '#fff',
                      border: '1px solid ' + BRIO_BORDER, borderRadius: '3px',
                      color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      pointerEvents: 'none', maxWidth: '260px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {dragTask.task || 'Task'}
                    </div>
                  );
                }
              }

              return null;
            })()}
          </DragOverlay>

          </DndContext>
        </div>
      )}

      {/* ── Allocation info hint ────────────────────────────────────── */}
      {selectedProjectId && !tasksHook.isLoading && (
        <div style={{
          marginTop: '12px', fontSize: '11px', fontWeight: 300, color: '#bbb', fontStyle: 'italic'
        }}>
          {allocatedUsers.length > 0
            ? allocatedUsers.length + ' team member' + (allocatedUsers.length !== 1 ? 's' : '') + ' allocated to this project in ' + currentWeekId
            : 'No team members allocated to this project in ' + currentWeekId + ' — allocate via the Focus Board'}
        </div>
      )}
    </div>
  );
}

// ── Table cell styles ──────────────────────────────────────────────────
var thStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '11px',
  fontWeight: 500,
  color: BRIO_GREY,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid ' + BRIO_BORDER,
  borderRight: '1px solid ' + BRIO_BORDER
};

var thCompact = {
  width: '1%',
  whiteSpace: 'nowrap'
};

var tdCompact = {
  width: '1%',
  whiteSpace: 'nowrap'
};

var tdStyle = {
  padding: '4px',
  verticalAlign: 'middle',
  borderRight: '1px solid ' + BRIO_BORDER
};
